import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LIMITS } from '../../constants/gameLimits';
import LineIcon from './LineIcons';
import useViewport from '../../hooks/useViewport';
import { RoomPickerList } from './RoomPicker';
import { InlineRoomDoor, InlineCalendarDoor, RoomVerbLane } from './ComposerDoors';
import { RestingBanner } from './AsleepLook'; // 🌙 W2b the asleep look
import { ASLEEP_COPY } from '../../utils/aiAsleep';
import CandleHop from './CandleHop'; // 🕯️ 09-24 the house's "is thinking" dots
import { roomHintLive, roomHintSeen, roomHintUsed, hintStore } from '../../utils/roomHint';
import { crowdMentionRows } from '../../utils/crowdInline';
import { ROOM_VERB_COMMANDS, roomVerbHere, roomVerbKnown, roomVerbBarLine } from '../../utils/roomVerbs';
import { picturesHint } from '../../utils/portedPictures'; // 💡 E2: "N left today" on /imagine /build /scene in a ported room
import { shareMenuItems, canShareScreen, SHARE_SCREEN_NOTE } from '../../utils/roomShellPage'; // 🧱 the room frame's labeled Share (09-29)
import { commandFitsPurpose } from '../../utils/spacePurpose'; // 🧭 10-02 a room's purpose keeps only the verbs that fit it

// Slash commands — "direct the space" (SLASH_COMMANDS_SPEC_2026_07_11.md).
// Non-competitive only. Card templates MUST mirror backend copy in
// justsomeguy-backend/src/ChatGame/spaceCommands.js — the client one is only
// the optimistic placeholder until the authoritative card arrives.
// 🎲 /roll (09-29, SPACES_REDESIGN §5.5c): mirrors backend SPACE_COMMANDS.roll
// (diceRoll.js has the contract). The server rolls, so there is NO card
// template: a client placeholder could only show a fake number. Untargeted
// (an "@" is never a die), text optional (bare /roll = 1d20). Every argHint
// example must parse on the backend (HumanInputForm.roll.test.js runs the real
// backend parser). DARK: the entry joins the list only when ROLL_COMMAND_LIVE
// flips, in the same ship as the backend's TOOL_DICE_ENABLED (off there, the
// server answers "Unknown command").
export const ROLL_COMMAND_LIVE = true;
export const ROLL_COMMAND = {
  name: 'roll', emoji: '🎲', argHint: '2d6+1 · d20+5 · adv',
  description: 'Roll any dice. Everyone sees it',
  minTargets: 0,
  noTargets: true,
};
export const SLASH_COMMANDS = [
  {
    // 🚪📷 /room (ROOM_CAMERA_SPEC Wave 4, 09-15): the per-viewer camera.
    // PRIVATE — no card template on purpose (a cardless command posts no
    // optimistic card and the backend posts nothing); the answer arrives in
    // the response body and renders as the chips-bar notice line.
    name: 'room', emoji: '🚪', argHint: 'porch (empty = list rooms)',
    description: 'Move your view to another room of the house',
    minTargets: 0,
    // houseOnly (09-17): lives only where its answer can render — hidden
    // from the palette AND the parser wherever houseRooms is null.
    houseOnly: true,
  },
  {
    // 👀 /look (09-17): private, client-answered, ZERO-LLM — the page
    // describes the room you're in from the payload it already holds
    // (GameStepPage intercept, grep roomLookText). Never reaches the API.
    name: 'look', emoji: '👀', argHint: '',
    description: 'Look around the room you are in',
    minTargets: 0,
    houseOnly: true,
  },
  {
    // 👥 10-01 people (HOUSE_OCTOBER_UIUX_BATCH_SPEC §3.4): the line under your name.
    // PRIVATE + cardless like /room (the reply is the notice line, no_wake).
    // `people`: listed only while the payload carries house.people (backend
    // HOUSE_PEOPLE_ENABLED), so a dark backend is never sent the word.
    name: 'status', emoji: '🟢', argHint: 'making tea (empty = yours, clear = remove)',
    description: 'Set the line under your name',
    minTargets: 0,
    noTargets: true,
    houseOnly: true,
    notPorted: true,
    people: true,
  },
  {
    // 💰 10-02 /till (Nina 10-01 night: "yes we want /till, but the actual till
    // itself kinda crowds everywhere"): the till's door is this command, never a
    // chip. Server-answered privately (backend houseTill.js): bare = the reading,
    // "add 3" adds up to the day's 3 coins. houseOnly: only the house has a till.
    name: 'till', emoji: '💰', argHint: 'add 1-3',
    description: 'See the house till, or add up to 3 coins a day',
    minTargets: 0,
    houseOnly: true,
  },
  {
    // 🔥 10-03 game nights (backend houseGameNight.js, HOUSE_CAMPFIRE_ENABLED): /tale drops ONE
    // sealed two-sentence tale in the campfire tin. Private and cardless (the server answers in the
    // notice line; a refusal comes back as an error and the draft stays). `tale`: it exists only
    // while the status payload says the tin is OPEN (house.campfire.open, a key a dark backend
    // never sends), so the palette, the parser, the "Try:" list and the argument bar all skip it
    // otherwise (taleHere). `noTargets`: an "@Fen" in a tale is part of the words.
    name: 'tale', emoji: '🔥', argHint: 'two sentences, sealed till the fire',
    description: 'Drop a sealed scary tale in the campfire tin',
    minTargets: 0,
    noTargets: true,
    houseOnly: true,
    tale: true,
  },
  {
    // 📋 10-14 /plan (backend housePlan.js, HOUSE_PLAN_ENABLED): invite up to 2 awake residents to start getting
    // ready for a night on the calendar (the 🗓️ door's "Plan it" sends the same thing). Cardless: the server
    // writes the card (it picks the night and who), and it answers privately when there is nothing to plan.
    // `plan`: listed only while the payload carries house.plan (a key a dark backend never sends), so the
    // palette, the parser, the "Try:" list and the argument bar all skip it otherwise (planHere).
    name: 'plan', emoji: '📋', argHint: 'corn maze @Fen (empty = the next night)',
    description: 'Get the house ready for a night on the calendar',
    minTargets: 0,
    houseOnly: true,
    plan: true,
  },
  {
    // 🍎🧺 /pick (09-19, HOUSE_CALENDAR_OUTINGS_SPEC §2.5): the orchard basket.
    // PRIVATE and cardless like /room (the answer is the notice line). It
    // exists only while the house is OUT at that outing AND you are standing
    // in it: `outing` gates the palette, the arg bar and the parser
    // (outingHere below), so the rest of the year typing it gets the honest
    // unknown-command line. Independent of the room-verbs flag on purpose.
    name: 'pick', emoji: '🍎', argHint: '',
    description: 'Pick an apple into the house basket',
    minTargets: 0,
    houseOnly: true,
    outing: 'orchard',
  },
  // 🌽🏮 09-28 maze (spec §8.5): /left + /right walk the corn maze, the /pick
  // shape exactly (private, cardless, one tap). Gated by outingHere to the maze
  // while you stand in it, so the rest of the year they are unknown commands.
  {
    name: 'left', emoji: '👈', argHint: '',
    description: 'Take the left path in the corn maze',
    minTargets: 0,
    houseOnly: true,
    outing: 'maze',
  },
  {
    name: 'right', emoji: '👉', argHint: '',
    description: 'Take the right path in the corn maze',
    minTargets: 0,
    houseOnly: true,
    outing: 'maze',
  },
  {
    name: 'talk', emoji: '🎬', argHint: '@local [@local] [about topic]',
    description: 'Get a local talking, or make two talk to each other',
    minTargets: 1,
    card: (issuer, t, text) => `🎬 ${issuer} wants ${t[0]}${t[1] ? ` and ${t[1]}` : ''} to talk${text ? ` about ${text}` : ''}!`,
  },
  {
    name: 'ship', emoji: '💘', argHint: '@local @local',
    description: 'Play matchmaker. Sparks will fly!',
    minTargets: 2,
    card: (issuer, t) => `💘 ${issuer} is playing matchmaker: ${t[0]} + ${t[1]}!`,
  },
  {
    name: 'gift', emoji: '🎁', argHint: '#item @local',
    description: 'Give a shelf item to a local. They keep it!',
    minTargets: 1, requiresText: true,
    // Backend rewrites text to "emoji #item from giver" — optimistic card shows
    // the raw handle for a beat, then the server card swaps in.
    card: (issuer, t, text) => `🎁 ${issuer} gave ${text} to ${t[0]}!`,
  },
  {
    name: 'drama', emoji: '🌶️', argHint: '@local @local [over thing]',
    description: 'Start a petty feud between two locals',
    minTargets: 2,
    card: (issuer, t, text) => `🌶️ ${issuer} is stirring the pot: ${t[0]} vs ${t[1]}!`,
  },
  {
    name: 'scene', emoji: '🎭', argHint: 'a storm knocks the power out',
    description: 'Drop a premise on the whole room',
    minTargets: 0, requiresText: true,
    card: (issuer, t, text) => `🎭 ${issuer} sets the scene: ${text}`,
  },
  {
    name: 'translate', emoji: '🌍', argHint: 'spanish (or pirate!)',
    description: 'Flip the room into another language or style',
    minTargets: 0, requiresText: true,
    card: (issuer, t, text) => `🌍 ${issuer} flips the room to ${text}!`,
  },
  // Content commands: no card template — the artifact itself (note/video/image)
  // is the visible result, so no optimistic event card is posted.
  {
    name: 'imagine', emoji: '✨', argHint: 'a haunted lighthouse',
    description: 'Conjure an image for the room',
    minTargets: 0, requiresText: true,
    card: (issuer, t, text) => `✨ ${issuer} is imagining: ${text}`,
  },
  {
    name: 'build', emoji: '🔨', argHint: 'a hot tub — or #stick + #stone',
    description: 'Build something (it stays!) or combine # items',
    minTargets: 0, requiresText: true,
    // Two #handles = crafting; the backend rewrites build→combine. Card copy
    // must match backend's combine.card for the optimistic swap.
    card: (issuer, t, text) => /#[a-z0-9][\w-]*\s*\+\s*#[a-z0-9][\w-]*/i.test(text)
      ? `⚗️ ${issuer} is combining ${text}...`
      : `🔨 ${issuer} started construction: ${text}`,
  },
  {
    // 🏡 notPlace (W2d): in a ported room's place page /watch opens the watch
    // party (the place-only entry below); this card-posting /watch steps aside.
    name: 'watch', emoji: '📺', argHint: 'lofi girl (or paste a link)', notPlace: true,
    description: 'Put a video on for the room',
    minTargets: 0, requiresText: true,
  },
  {
    name: 'watchparty', emoji: '🍿', argHint: 'a movie trailer (or paste a link)',
    description: 'Start a watch party on the stage',
    minTargets: 0, requiresText: true,
  },
  {
    name: 'listen', emoji: '🎧', argHint: 'rainy jazz',
    description: 'Put music on for the room',
    minTargets: 0, requiresText: true,
  },
  {
    // Born /jukebox, display-renamed /aux 2026-08-17 (the aux is the fiction
    // everywhere else: "grabbed the aux" cards, house_aux, the guest grab).
    // The old name stays typed-in-able forever via aliases — matched by the
    // parser and the arg-hint bar, never shown in the palette. Backend
    // normalizes either name to the internal id 'jukebox'
    // (AUX_COMMAND_RENAME_2026_08_17.md).
    name: 'aux', aliases: ['jukebox'], emoji: '🎶', argHint: 'paste a link or name a song',
    description: 'Queue a track on the room speakers',
    minTargets: 0, requiresText: true,
    // Backend rewrites text to the resolved track title — optimistic card
    // shows the raw words for a beat, then the server card swaps in.
    card: (issuer, t, text) => `🎶 ${issuer} put a record on: ${text}`,
  },
  {
    name: 'note', emoji: '📌', argHint: 'pizza friday is sacred',
    description: 'Pin a note to the room',
    minTargets: 0, requiresText: true,
  },
  // Content command (no card template): the playground card IS the result.
  {
    name: 'code', emoji: '⌨️', argHint: 'a starfield (optional)',
    description: 'Open a code sandbox the room can run',
    minTargets: 0,
  },
  ...(ROLL_COMMAND_LIVE ? [ROLL_COMMAND] : []),
  // ── Mod-only (space creator) — palette + parser gate on isSpaceMod; the
  // backend enforces the creator check regardless.
  {
    name: 'quest', emoji: '⚔️', argHint: 'find the lost teaspoon', modOnly: true,
    description: 'Mod: send the room on a quest (stakes a shelf reward!)',
    minTargets: 0, requiresText: true,
    card: (issuer, t, text) => `⚔️ QUEST from ${issuer}: ${text} — a reward just hit the shelf!`,
  },
  {
    // 🏡 notPorted (09-25): a ported room's owner IS the mod, and this would
    // hand their resident The Terminal's coding directive. Hidden there; a
    // place page has the resident's own /task (below, W5), and notPlace steps
    // this one aside INSIDE the parser's find (a second entry with the same
    // name needs the gate there: find returns the first match).
    name: 'task', emoji: '⌨️', argHint: 'write fizzbuzz without using %', modOnly: true, notPorted: true, notPlace: true,
    description: 'Mod: hand the coders a task to work live',
    minTargets: 0, requiresText: true,
    card: (issuer, t, text) => `⌨️ NEW TASK from ${issuer}: ${text}`,
  },
  {
    name: 'drop', emoji: '🎁', argHint: '', modOnly: true,
    description: 'Mod: drop a basic item on the shelf',
    minTargets: 0,
    card: (issuer) => `🎁 ${issuer} restocked the shelf!`,
  },
  {
    name: 'tinker', emoji: '🧸', argHint: 'a fish tank you can feed',
    description: 'The builder makes you a tiny toy',
    minTargets: 0, requiresText: true,
    // Copy must match backend SPACE_COMMANDS.tinker.card (optimistic swap)
    card: (issuer, t, text) => `🧸 ${issuer} is tinkering: ${text}`,
  },
  // FRONTEND-orchestrated (GameStepPage launchChallenge): creates a real 1v1
  // Roast Battle via the Games-tab instant-create path — never a backend
  // space_command. No card template (the announce message is the artifact).
  {
    name: 'challenge', emoji: '⚔️', argHint: '@local',
    description: 'Duel a local in a Roast Battle',
    minTargets: 1,
  },
  // FRONTEND-orchestrated (GameStepPage launchDoodle): mod sets up an easel —
  // instant Doodle Bug round with three locals + you. Mod-only (Nina 07-15:
  // the mod starts doodles; AI-initiated = parked v2).
  {
    name: 'doodle', emoji: '🖍️', argHint: '', modOnly: true,
    description: 'Mod: start a Doodle Bug round with the locals',
    minTargets: 0,
  },
  {
    // 🐛 /bug (09-20): private, client-answered, never reaches the chat API.
    // GameStepPage intercepts it and opens the app-level bug report modal with
    // whatever was typed after the word (grep openBugReport). `noTargets`: an
    // "@Fen" in a bug description is part of the words, never a target.
    name: 'bug', emoji: '🐛', argHint: 'what went wrong',
    description: 'Report a bug to the team (private)',
    minTargets: 0,
    noTargets: true,
  },
  // 🏡⋯ The place's extras (W2d, PORTED_HOME_HANDOFF §0-ter, Nina: "⋯ menu +
  // slash commands"): client-only like /bug. GameStepPage opens the existing
  // surface where the painting is and posts NOTHING to the room. placeOnly:
  // a ported room's place page only (the ⋯ menu names the same six doors).
  {
    name: 'watch', emoji: '🍿', argHint: '', placeOnly: true,
    description: 'Watch something together',
    minTargets: 0, noTargets: true,
  },
  {
    name: 'live', emoji: '🔴', argHint: '', placeOnly: true,
    description: 'Go live in the room',
    minTargets: 0, noTargets: true,
  },
  {
    // deskOnly: a phone has no Story place for a ported room (the recap place is The Terminal's)
    name: 'story', emoji: '📖', argHint: '', placeOnly: true, deskOnly: true,
    description: 'Your story so far',
    minTargets: 0, noTargets: true,
  },
  {
    // 📋 W5 (PORTED_HOME_HANDOFF §8): the resident's own /task, client-only
    // like /bug. GameStepPage opens the journal's Tasks page with the words
    // typed after it (the timing chips live there) and posts NOTHING to the
    // room. ownerOnly: the place owner's alone (all three lookups; placeOk).
    name: 'task', emoji: '📋', argHint: 'a poem about the harvest moon', placeOnly: true, ownerOnly: true,
    description: 'Something to do, now or later',
    minTargets: 0, noTargets: true,
  },
  // 🕯️🚪 Room verbs (ROOM_VERBS_PEEK_SPEC §3): commands you can only do in ONE
  // room. The entries come from the parity-pinned mirror (utils/roomVerbs.js);
  // `rooms` gates them to the room you are standing in, on top of houseOnly.
  ...ROOM_VERB_COMMANDS,
];

/**
 * HumanInputForm
 * UI component for human players to submit their actions during their turn
 * Also supports "always available" mode for free-form chat outside of turns
 */
/** 🏡 W2d: place-only commands live only on a ported room's place page, and a notPlace one steps aside there. */
// 📋 W5: `ownerOnly` = the place owner's alone (a ported room's `isSpaceMod`
// is true for any seated human in their own browser, so modOnly can't hide it)
export const placeOk = (spec, placeRoom, phone = false, placeOwner = true) => (spec && spec.placeOnly
  ? (!!placeRoom && !(spec.deskOnly && phone) && !(spec.ownerOnly && !placeOwner))
  : !(spec && spec.notPlace && placeRoom));
/** 🍎 Outing commands live only while the house is out THERE and you are in it. */
export const outingHere = (spec, houseRooms) => !spec || !spec.outing
  || (!!houseRooms && houseRooms.outing === spec.outing && houseRooms.current === spec.outing);
/** 🔥 10-03 /tale lives only while the campfire's tin is open (house.campfire.open). */
export const taleHere = (spec, houseRooms) => !spec || !spec.tale
  || (!!houseRooms && !!houseRooms.campfire && houseRooms.campfire.open === true);
/** 📋 10-14 /plan lives only while the payload carries house.plan. */
export const planHere = (spec, houseRooms) => !spec || !spec.plan || (!!houseRooms && !!houseRooms.plan);
// 🌿 A palette row that belongs to the room you are in: a room verb or an
// outing command (only ones that already passed roomVerbHere / outingHere).
export const isHereCommand = (spec) => !!spec && (Array.isArray(spec.rooms) || !!spec.outing);
/** Stable: this room's commands first, everything else after, each in its order. */
export const hereFirst = (list) => [...list.filter(isHereCommand), ...list.filter((c) => !isHereCommand(c))];

// 🧱 The room frame's Share button + menu (09-29; `share` prop only). The
// frame's neutral chrome (C's tokens, rings not shadows). Inside rooms the
// generated customCSS styles every button (some rules !important) and every
// class holding card/panel/chip/btn/button/…: `hif-shr*` holds none of them,
// each rule is (0,3,0) with !important on what a theme touches, and --prop-bg
// feeds ThemeStyleInjector's data-prop-ui shield. No global border-box here.
const SHARE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const SHARE_MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const SHARE_CSS = `
.hif-shr, .hif-shr * { box-sizing: border-box; }
.hif-shr { position: relative; flex: none; display: flex; align-items: center; }
.hif-shr .hif-shrb.hif-shrb { all: unset; box-sizing: border-box !important; display: inline-flex !important; align-items: center; gap: 7px; width: auto !important; min-width: 0 !important; height: 36px !important; padding: 0 12px !important; margin: 0 !important; border: 0 !important; border-radius: 9px !important;
  background: transparent !important; --prop-bg: transparent; color: var(--rs-acc-t, #E6E8EE) !important; box-shadow: inset 0 0 0 1.5px var(--rs-acc-line, rgba(255,255,255,0.22)) !important; font: 600 13px/1 ${SHARE_FONT} !important;
  letter-spacing: normal !important; text-transform: none !important; text-shadow: none !important; transform: none !important; opacity: 1 !important; white-space: nowrap; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.hif-shr[data-phone="1"] .hif-shrb.hif-shrb { height: 40px !important; padding: 0 11px !important; }
.hif-shr .hif-shrb.hif-shrb:hover, .hif-shr .hif-shrb.hif-shrb[aria-expanded="true"] { background: var(--rs-acc-soft, rgba(255,255,255,0.08)) !important; --prop-bg: var(--rs-acc-soft, rgba(255,255,255,0.08)); }
.hif-shr .hif-shrb.hif-shrb[disabled] { opacity: .6 !important; cursor: default; }
.hif-shrm { position: absolute; bottom: calc(100% + 14px); right: 0; z-index: 1000; width: 250px; max-width: calc(100vw - 24px); padding: 6px; border-radius: 12px; background: #14171D; box-shadow: inset 0 0 0 1px #262B34, 0 18px 48px rgba(0,0,0,0.55); text-align: left; }
.hif-shrh { padding: 8px 10px 6px; font: 500 10.5px/1.2 ${SHARE_MONO}; letter-spacing: .09em; text-transform: uppercase; color: #8B92A0; text-shadow: none; }
.hif-shr .hif-shri.hif-shri { all: unset; box-sizing: border-box !important; display: flex !important; align-items: center; gap: 10px; width: 100% !important; min-width: 0 !important; min-height: 40px !important; height: auto !important; padding: 8px 10px !important; margin: 0 !important; border: 0 !important; border-radius: 8px !important;
  background: transparent !important; --prop-bg: transparent; color: #ECEEF2 !important; box-shadow: none !important; font: 500 14px/1.2 ${SHARE_FONT} !important;
  letter-spacing: normal !important; text-transform: none !important; text-shadow: none !important; transform: none !important; opacity: 1 !important; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.hif-shr .hif-shri.hif-shri:hover { background: #1A1E25 !important; --prop-bg: #1A1E25; }
.hif-shr .hif-shrb.hif-shrb:focus-visible, .hif-shr .hif-shri.hif-shri:focus-visible { outline: 2px solid #5CE1E6 !important; outline-offset: 2px !important; }
.hif-shric { display: inline-grid; place-items: center; width: 18px; flex: none; color: #8B92A0; }
.hif-shrl { flex: 1 1 auto; min-width: 0; }
.hif-shrk { flex: none; font: 500 11px/1 ${SHARE_MONO}; color: #8B92A0; white-space: nowrap; }
.hif-shrn { display: flex; align-items: flex-start; gap: 8px; margin: 4px 0 0; padding: 8px 10px 4px; border-top: 1px solid #262B34; font: 500 11.5px/1.35 ${SHARE_FONT}; color: #8B92A0; text-shadow: none; }
.hif-shrn svg { flex: none; margin-top: 1px; }
`;
const shareIcon = (children) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{children}</svg>
);
const SHARE_ICONS = {
  doc: shareIcon(<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5" />),
  image: shareIcon(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>),
  video: shareIcon(<><rect x="2" y="6" width="14" height="12" rx="2" /><path d="M22 8l-6 4 6 4z" /></>),
  screen: shareIcon(<><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></>),
  eye: shareIcon(<><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>),
  send: shareIcon(<><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></>),
};
// 🧱 The room frame's composer LOOK (09-30, `framed` prop only; mockup frame A
// ".cbox"): ONE box on the frame's own tokens (--rs-*, inherited from the
// frame around the composer slot; the fallbacks are the same dark values).
// The ring sits on the whole row while the line has focus, the line's own
// ring (today's .hif-box:focus-within) steps aside, the placeholder is muted.
// Same armor as the Share button: `hif-fr*` holds no word a room theme targets.
const FRAME_CSS = `
.hif-freechat .hif-frow { box-shadow: inset 0 0 0 1px var(--rs-line, #262B34); }
.hif-freechat .hif-frow:focus-within { box-shadow: inset 0 0 0 1px var(--rs-acc-line, rgba(79,140,255,0.4)), 0 0 0 3px var(--rs-acc-soft, rgba(79,140,255,0.15)); }
.hif-freechat .hif-frow .hif-box:focus-within { box-shadow: none; }
.hif-freechat .hif-frow textarea::placeholder { color: var(--rs-muted, #8B92A0) !important; }
.hif-freechat .hif-frow .hif-frt:hover:not([disabled]) { background: var(--rs-on, #1F242D) !important; }
.hif-freechat .hif-frow :is(.hif-frt, .hif-frs):focus-visible { outline: 2px solid var(--rs-focus, #5CE1E6); outline-offset: 2px; }
`;

// 📝 Draft persistence (08-03 chat-tail audit): the composer unmounts on
// fold / place swap / desktop chip switch, and `message` state died with it —
// half-typed messages silently vanished. Module-scope store keyed by player
// survives remounts but dies with the tab (deliberately NOT localStorage — a
// stale draft resurfacing next session would read as a bug). Submit clears
// message → the effect stores '' → the draft is consumed; the prefill seed
// already yields to non-empty text, so a restored draft blocks it correctly.
const composerDrafts = new Map();

// Drop a player's stored draft ONLY if it still exactly equals `text` — used
// to retract a tutorial seed after skip/end without touching anything the
// player actually typed. Safe no-op when the draft was edited or consumed.
export const retractComposerDraft = (playerId, text) => {
  const k = playerId || 'anon';
  if (text && composerDrafts.get(k) === text) composerDrafts.delete(k);
};

const HumanInputForm = ({
  // 📣 Live room T9: ghost aliases offered in the @ dropdown (GameStepPage
  // passes status.crowd_react.aliases; empty everywhere else)
  crowdAliases = [],
  playerId,
  playerName,
  availableActions = [],
  tokensLeft = 0,
  onSubmit,
  onSubmitInterjection = null,  // Callback for interjections (free chat)
  disabled = false,
  theme = {},
  alwaysAvailable = false,  // If true, form is always available for free chat
  isYourTurn = true,  // If false, shows simplified free chat mode
  playRoundButton = null,  // Optional Play Round button rendered above Send Message
  interjectionsRemaining = 3,  // Messages remaining this round (3 per round limit)
  isSpaceMod = false,  // Space creator: unlocks mod-only commands (/quest /drop). Backend enforces regardless.
  onPaletteOpen = null,  // Fires when the "/" actions palette opens (tour beat 4 advances on it)
  onOpenDoodleTurn = null,  // 🐛 Doodle Bug: open the 2-stroke turn canvas (GameStepPage owns the modal + submit)
  imposterMode = false,  // Simplified one-word input for Glitch mode
  competitiveMode = false,  // Simplified message input for competitive mode
  pendingTurnType = null,  // Backend pending_interaction.type ('imposter_word' | 'vote' | ...). Vote turns recovered via status poll (missed WS) used to render the ONE-WORD form — a vote presented as a word prompt (Golden Glitch 07-12). With 'vote' + onSubmitVote set, the imposter branch renders a player-pick instead.
  onSubmitVote = null,  // async ({ voted_for, target_name }) — GameStepPage's handleVoteSubmit (interaction + submitVote fallbacks)
  turnDeadline = null,  // Epoch ms when the turn expires (shows countdown timer)
  voteFold = false,     // ⚖️ One CTA at a time (Nina 07-30): the lineup wants a vote — idle composer vanishes, an OPEN turn folds to one honest line w/ its real countdown
  suppressIdle = false, // ⚖️ One-card dock (Nina 07-30): the roll card's footer carries "waiting" — drop the idle box (never when playRoundButton is actionable)
  players = [],  // Other players for @mention autocomplete
  onPoke = null,  // Callback to trigger AI play round (non-competitive "poke")
  isPoking = false,  // Whether a poke is currently in progress
  trayMode = false,  // 📱 places nav (Nina 08-01): mobile non-comp folds ⚡📎🖍 behind one ＋ (icon-dock tray); poke joined the tray 08-02
  onOpenShelf = null,  // ＋ tray's Shelf verb — opens the wood drawer (page owns the state)
  locked = false,  // Turn input is gated until the player rolls their Fate die (shows a lock on the box)
  onRollDie = null,  // Roll the Fate die (the unrolled prompt overlays the message box on your turn)
  diceRolling = false,  // Die tumble animation in progress
  diceLabel = 'FATE ROLL',  // Concise die label shown on the roll overlay
  prefillNonce = 0,    // 🪧 bump to re-apply the SAME seed (room plate chips: tapping /aux twice must work)
  prefillText = null,  // Opening line to seed the message box with (round 1) — set once, never clobbers edits
  bannedWords = null,  // Dice Power Word Ban v2: array of { word, source: 'targeted'|'fumble' } banned for the viewer this round (null/[] = none)
  sentenceCap = null,  // Hard per-message length cap for this game (AI prompts enforce ~40 words; here = hint + soft char limit)
  replyCredit = false,  // Competitive rounds 2+: this (second) credit should respond to another player by name
  onOpenDrawing = null,  // Opens the chat-canvas doodle modal (non-competitive only; parent passes null elsewhere)
  onUploadFile = null,  // Shares a picked file (image/video/pdf/doc) into the chat (non-competitive only; parent passes null elsewhere)
  sideQuest = null,  // Human side quest { label, text, points, done } — dare card above the composer; done = live-claimed this round (LEGACY for whodunnit: post-07-31 games send null; betReminder replaced it)
  betReminder = null,  // 🎯 {word, points} while the viewer's accepted detective's bet is live THIS round — the one line the composer keeps (Nina 07-31: you opted in, so the reminder is wanted, not clutter)
  shelfItems = [],  // Crafting shelf (/build ledger): [{ name, handle, emoji }] — drives # autocomplete
  noirDocket = null,  // 🗂 Whodunnit docket mode (Nina rev 3, 07-29): { headline, facts: [{t,c,whisper?}] }. When set, the competitive form renders as THE docket: manila tab header (timer inside), headline + fact line, slim bet, skip as a link. null = classic chrome, all other game types unaffected.
  thinkingStatus = null,  // 📱 "X is thinking" moves INTO the composer strip while the dock is open (Nina 08-06) — name string or null; mobile free-chat only, GameStepPage suppresses its floating pill while this is set
  thinkingCandle = false, // 🕯️ the Claude House (09-24, "Between Rooms" D): the candle hop replaces 🤔 in both lines below
  watchPartyOn = false,  // 📺 THIS user has the watch party open (Nina 08-27, tightened from the 08-13 space-level read): placeholder teaches @watchparty only while the sender could actually see their quip land (overlay renders only inside the theater)
  portedRoom = false,  // 🏡 a ported 1:1 room: hides notPorted commands (The Terminal's /task, 09-25)
  roomPurpose = null,  // 🧭 10-02 status.purpose ('learn' · 'research' · …): hides the verbs that don't fit the room (utils/spacePurpose.commandFitsPurpose); null = every verb
  placeRoom = false,  // 🏡 W2d: a ported room's PLACE page (behind the switch): the place-only /watch /live /story, and the mockup's compact desktop composer
  placeOwner = true,  // 📋 W5: the place page's OWNER (ownerOnly commands: the resident's /task)
  pictures = null,    // 💡 E2: a ported room's status `pictures` ({ left, per_day }): the "N left today" hint on /imagine /build /scene; null elsewhere
  placeName = null,   // 🏡 W2d: the resident's name for "Say something to Kenley…" (place page only)
  onComposerActivity = null,  // 🏡 W2d: (active) => void, true while the composer is focused or holds a draft (the stage shrinks as you chat)
  houseRooms = null,  // 🚪 /room (09-15; picker 09-18): { rooms: [{id,label}], current: roomId, rows: roomPickerRows(), onGo: (id) => move, door: { icon, label, open, onToggle } } — house spaces only; drives the room LIST under a committed "/room "
  eventStrip = null,   // ⏳🎬 the countdown line above the box (09-22, Nina): utils/calendarView.eventStrip() + { onGo } — house spaces only, null hides it
  resting = null,  // 🌙 W2b (09-26): utils/aiAsleep.restingBanner(status.ai_power) while the AIs sleep at $0: the banner above the box, the note placeholder, Poke off; null = awake (byte-identical)
  share = null,  // 🧱 the room frame's labeled "Share" (09-29, SPACES_REDESIGN §5.5a): { onScreen?: () => void } from the ONE composer the frame mounts; null everywhere else (no button). Doc · Image · Video ride onUploadFile; Screen shows only with onScreen, on desktop, where getDisplayMedia exists
  framed = null,  // 🧱 the room frame's composer LOOK (09-30, mockup rooms-by-purpose.html frame A): { name } from the ONE composer the frame mounts: one box on the frame's tokens, [tools] [the line] [Share] [Send], phones fold the tools behind a ＋ (the tray). null everywhere else = today's composer, byte for byte
  houseCalendar = null  // 📅 house calendar door (09-19): { door: { text, hot, title, open, onToggle } } from utils/calendarView.doorLabel — house spaces only, absent until the backend ships house.calendar
}) => {
  // Mobile detection – bump input font to ≥16px to prevent iOS auto-zoom.
  // Round 5 (08-03): shared useViewport store replaces the private listener.
  const { isMobile } = useViewport();

  // Inject placeholder color style once so text is visible on dark themed backgrounds
  useEffect(() => {
    const id = 'human-input-form-placeholder-style';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      // hif-freechat = the non-competitive space chat box only: brighter placeholder
      // + a static focus ring so the box reads as "type here" against busy space art
      // (user call 07-11). Static styles only — no animation cost.
      style.textContent = `.human-input-form textarea::placeholder, .human-input-form input::placeholder { color: rgba(255,255,255,0.4) !important; }
.hif-freechat textarea::placeholder, .hif-freechat input::placeholder { color: rgba(255,255,255,0.75) !important; }
.hif-freechat .hif-box:focus-within { box-shadow: 0 0 0 2px rgba(255,255,255,0.25); }
.hif-door:hover { color: #e9b565 !important; background: rgba(233,181,101,0.12) !important; }
.hif-door:focus-visible { outline: 2px solid #ffd98a; outline-offset: 1px; }
[data-room-floor]::-webkit-scrollbar { display: none; }
[data-floor-chip]:hover { border-color: #e9b565 !important; color: #ffd98a !important; }
[data-floor-chip]:focus-visible { outline: 2px solid #ffd98a; outline-offset: 1px; }
@media (max-width: 339px) { [data-calendar-weather] { display: none; } }`; // 🧭 10-01 composer: the smallest phones drop the weather word (by width only), so the doors + ＋ ➤ keep one line
      document.head.appendChild(style);
    }
  }, []);

  // Countdown timer
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    if (!turnDeadline || !isYourTurn) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((turnDeadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [turnDeadline, isYourTurn]);

  // File upload (📎 next to the crayon): hidden input + in-flight flag
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  // ＋ tray (trayMode): closed on mount, closes on any pick or tap-away
  const [trayOpen, setTrayOpen] = useState(false);
  // 🧱 the room frame's Share menu (only while `share` is set): its own hidden
  // input (the 📎's keeps its accept list), the filter set per pick
  const shareInputRef = useRef(null);
  const shareWrapRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    if (!shareOpen) return undefined;
    const onDown = (e) => { if (shareWrapRef.current && !shareWrapRef.current.contains(e.target)) setShareOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setShareOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey); };
  }, [shareOpen]);

  // Form state
  const [actionType, setActionType] = useState('public_message');
  // Restore the surviving draft on mount; mirror every change back (see
  // composerDrafts at module scope).
  const [message, setMessage] = useState(() => composerDrafts.get(playerId || 'anon') || '');
  useEffect(() => {
    composerDrafts.set(playerId || 'anon', message);
  }, [playerId, message]);
  // 👀 Focus gating (Nina 08-04): every suggestion surface (slash palette,
  // arg-hint bar, @/# dropdowns) is DERIVED from the draft text — with
  // drafts persisting across unmounts (08-03), a restored "/gift …" summoned
  // the arg-hint bar with nobody in the composer (the tutorial tour
  // auto-expands the fold, so it popped mid-tour "out of nowhere" — her
  // screenshot). Suggestions render only while a composer field owns focus;
  // the 160ms blur grace covers focus moving BETWEEN composer fields, and
  // chip/palette taps already keep focus via mousedown-preventDefault.
  const [composerFocused, setComposerFocused] = useState(false);
  // 🆕🚪 "New! Try /room" (Nina 09-18): a red hint INSIDE the message box, in
  // front of the usual placeholder. House spaces only (houseRooms), per
  // device, and it RETIRES — after this viewer's first /room use, or 14 days
  // after they first saw it (utils/roomHint). Read once on mount; the clock
  // starts the first time the hint can actually be on screen.
  const [roomHintAlive, setRoomHintAlive] = useState(() => roomHintLive(hintStore(), Date.now()));
  const inHouseSpace = !!houseRooms;
  useEffect(() => {
    if (inHouseSpace && roomHintAlive) roomHintSeen(hintStore(), Date.now());
  }, [inHouseSpace, roomHintAlive]);
  // ⏳🎬 09-22: the countdown line's per-device ✕ (key|phase; a new phase shows it again)
  const EVENT_STRIP_STORE = 'hif_event_strip_hidden';
  const [stripHiddenKey, setStripHiddenKey] = useState(() => { try { return window.localStorage.getItem(EVENT_STRIP_STORE) || ''; } catch (e) { return ''; } });
  const hideEventStrip = useCallback((k) => { setStripHiddenKey(k); try { window.localStorage.setItem(EVENT_STRIP_STORE, k); } catch (e) { /* private mode */ } }, []);
  const retireRoomHint = useCallback(() => {
    roomHintUsed(hintStore(), Date.now());
    setRoomHintAlive(false);
  }, []);
  const blurGraceRef = useRef(null);
  const handleComposerFocus = useCallback(() => {
    if (blurGraceRef.current) { clearTimeout(blurGraceRef.current); blurGraceRef.current = null; }
    setComposerFocused(true);
  }, []);
  const handleComposerBlur = useCallback(() => {
    if (blurGraceRef.current) clearTimeout(blurGraceRef.current);
    blurGraceRef.current = setTimeout(() => setComposerFocused(false), 160);
  }, []);
  useEffect(() => () => { if (blurGraceRef.current) clearTimeout(blurGraceRef.current); }, []);
  // 🏡 W2d: the place page's stage shrinks while you TYPE ("grows back when
  // you stop", Nina): the box has focus AND holds a draft or saw input in the
  // last 3 s (clicking in counts as starting). A box that keeps focus after a
  // send lets go 3 s later; a draft left in a blurred box never holds it (you
  // went to read the journal). The 160ms blur grace debounces a focus hop
  // between composer fields; an unmount (Story) lets the stage grow back.
  const draftLive = !!message.trim();
  const [recentInput, setRecentInput] = useState(false);
  useEffect(() => {
    if (!composerFocused) { setRecentInput(false); return undefined; }
    setRecentInput(true);
    const t = setTimeout(() => setRecentInput(false), 3000);
    return () => clearTimeout(t);
  }, [composerFocused, message]);
  const activityRef = useRef(onComposerActivity);
  activityRef.current = onComposerActivity;
  useEffect(() => { if (activityRef.current) activityRef.current(composerFocused && (draftLive || recentInput)); }, [composerFocused, draftLive, recentInput]);
  useEffect(() => () => { if (activityRef.current) activityRef.current(false); }, []);
  const [thoughts, setThoughts] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnSubmitted, setTurnSubmitted] = useState(false); // Stay disabled until turn ends
  const [error, setError] = useState(null);
  // 📎 ONE file pick → upload lane for the 📎 and the room frame's Share (its
  // own input only sets a narrower accept filter): one upload at a time, the
  // page's onUploadFile, its error in the composer.
  const uploadPickedFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || uploadingFile) return;
    setUploadingFile(true);
    setError(null);
    try {
      await onUploadFile(file);
    } catch (err) {
      setError(err.message || 'Failed to share file');
    } finally {
      setUploadingFile(false);
    }
  };
  // ⏱ Frequency-limiter cooldown (Nina 08-20). A 429 from the interjection
  // endpoint carries real seconds (err.retryAfterSec, from the backend's
  // retry_after) — this is the one message lock with an honest clock, unlike
  // the per-round budget which resets on round rollover. Epoch ms when the
  // window reopens; the ticker effect below re-renders each second and
  // clears it at zero. ⚠️ The limiter is per warm Lambda container, so the
  // wait is worst-case: a retry may succeed early on another container —
  // never make the countdown gate anything but the Send button.
  const [rateLimitedUntil, setRateLimitedUntil] = useState(null);
  const [, forceCooldownTick] = useState(0);
  useEffect(() => {
    if (!rateLimitedUntil) return undefined;
    const t = setInterval(() => {
      if (Date.now() >= rateLimitedUntil) setRateLimitedUntil(null);
      else forceCooldownTick(n => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [rateLimitedUntil]);
  // 🔄 Refill moment (Nina 08-20, "messages refilled" ruling — no emoji).
  // The budget coming back is otherwise invisible: the box just quietly
  // re-enables on a status poll. Pairs with the "this round" copy — the chip
  // IS the round boundary a non-comp player can see. Gated on a FULL lockout
  // (prev 0 → >0): a 1→3 refill nobody felt would be noise. Two effects on
  // purpose: hanging the hide-timer's cleanup off the detector effect would
  // cancel the timer the next time the count moves and freeze the chip open.
  const [showRefilled, setShowRefilled] = useState(false);
  const prevRemainingRef = useRef(interjectionsRemaining);
  useEffect(() => {
    const prev = prevRemainingRef.current;
    prevRemainingRef.current = interjectionsRemaining;
    if (prev === 0 && interjectionsRemaining > 0) setShowRefilled(true);
  }, [interjectionsRemaining]);
  useEffect(() => {
    if (!showRefilled) return undefined;
    const t = setTimeout(() => setShowRefilled(false), 5000);
    return () => clearTimeout(t);
  }, [showRefilled]);
  // Free-chat send attempted while the parent lock (`disabled`, e.g. an AI turn
  // in flight) was on. The message stays in the box, a visible chip says it's
  // queued, and the effect below fires it the moment the lock releases. Exists
  // because a send click racing the lock used to die silently (5g.25 smoke2
  // send-drop): the click hit an already-disabled button, nothing told the
  // sender, and the composed message never went anywhere.
  const [queuedSend, setQueuedSend] = useState(false);
  // ⏎ Armed by the mention-dropdown Enter handler when a fully-typed @name
  // should complete AND send in one press — see handleKeyDownWithMention.
  const [mentionAutoSend, setMentionAutoSend] = useState(false);

  // @mention autocomplete state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedMentionIdx, setSelectedMentionIdx] = useState(0);
  const textareaRef = useRef(null);
  // 🧱 the room frame's line grows with what is typed (one line at rest, up to
  // its 140px cap, then it scrolls). A DOM height, never a React style key, so
  // nothing else re-renders. `framed` only: every other composer is untouched.
  // (a boolean dep: the page hands a fresh `framed` object every render, and
  // a height read forces layout, so it runs on a typed change only)
  const framedLine = !!framed;
  useEffect(() => {
    if (!framedLine) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [framedLine, message]);
  // 📐 Narrow-DESKTOP composer (Nina 08-14 screenshot): the desktop row keeps
  // fixed button columns beside the textarea, so in a narrow CONTAINER the
  // textarea shrinks to a sliver and the placeholder wraps clipped. Below
  // 500px of measured form width the layout goes mobile-shaped: full-width
  // taller textarea, buttons on one strip beneath. Container width, not
  // viewport (house rule) — this form lives in columns whose width moves
  // independently of the window. Phones keep their own branches untouched.
  // CALLBACK ref, not a mount-once effect (verify pass 08-14): the free-chat
  // form is one of several branches, and a branch flip (your-turn round
  // trip, comp/spectator states) swaps or removes the node — an effect armed
  // once at mount either never attached or froze narrowDesktop on a
  // detached node. React calls this with null on detach, so the observer
  // always tracks the LIVE node and the flag resets when the form is gone.
  const [narrowDesktop, setNarrowDesktop] = useState(false);
  const narrowRoRef = useRef(null);
  const formRootRef = useCallback((node) => {
    if (narrowRoRef.current) { narrowRoRef.current.disconnect(); narrowRoRef.current = null; }
    if (!node || isMobile || typeof ResizeObserver === 'undefined') {
      setNarrowDesktop(false);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const w = (entries[0] && entries[0].contentRect && entries[0].contentRect.width) || node.offsetWidth || 0;
      setNarrowDesktop(w > 0 && w < 500);
    });
    ro.observe(node);
    narrowRoRef.current = ro;
  }, [isMobile]);
  const mentionDropdownRef = useRef(null);

  // Inside a slash command, @ can only target locals — humans get rejected by
  // the server anyway ("X is a real human!"), so don't offer them. is_human is
  // the real flag; the 'Human Player' model string is the legacy fallback.
  // ⚠️ This deliberately also excludes visiting AGENTS, which carry is_human
  // for engine reasons (utils/playerKind) — and that is CORRECT here, not the
  // is_human bug: a directive works by injecting an impulse into a local's
  // prompt, and an external agent has no prompt we author. Plain @mentions
  // outside a slash command are unaffected (note the `draftIsCommand &&`).
  const draftIsCommand = message.trim().startsWith('/');
  const filteredSeatPlayers = players.filter(p =>
    p.name && p.name.toLowerCase().startsWith(mentionQuery.toLowerCase())
    && !(draftIsCommand && (p.is_human || p.model === 'Human Player'))
  );
  // 📣 Live room T9: crowd aliases ride the SAME list, after the seats, so the
  // keyboard nav, Enter-to-send and the render below need no second path. A
  // crowd row only ever inserts text (rules + pins: utils/crowdInline.js).
  const filteredPlayers = crowdAliases.length
    ? [...filteredSeatPlayers, ...crowdMentionRows({ aliases: crowdAliases, query: mentionQuery, players, draftIsCommand })]
    : filteredSeatPlayers;

  // # autocomplete (crafting shelf) — same machinery as @, sourced from the
  // space's /build ledger. Handles mirror the backend's normalizeHandle.
  const [showHashDropdown, setShowHashDropdown] = useState(false);
  const [hashQuery, setHashQuery] = useState('');
  const [hashStartPos, setHashStartPos] = useState(0);
  const [selectedHashIdx, setSelectedHashIdx] = useState(0);
  const itemHandle = (item) => item.handle
    || String(item.name || '').toLowerCase().replace(/^(a|an|the)\s+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const filteredShelf = shelfItems.filter(it =>
    itemHandle(it) && itemHandle(it).startsWith(hashQuery.toLowerCase())
  ).slice(0, 6);

  // Detect @mention in textarea input
  const handleMessageChange = useCallback((e) => {
    const val = e.target.value;
    setMessage(val);

    const cursorPos = e.target.selectionStart;
    // Walk backwards from cursor to find @ that starts a mention
    let atPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (val[i] === '@') {
        // @ must be at start of string or preceded by whitespace
        if (i === 0 || /\s/.test(val[i - 1])) {
          atPos = i;
        }
        break;
      }
      if (/\s/.test(val[i])) break; // hit whitespace before finding @
    }

    if (atPos >= 0) {
      const query = val.slice(atPos + 1, cursorPos);
      setMentionQuery(query);
      setMentionStartPos(atPos);
      setShowMentionDropdown(true);
      setSelectedMentionIdx(0);
    } else {
      setShowMentionDropdown(false);
    }

    // Same walk-back for '#' (crafting shelf items). '@' and '#' can't both be
    // the active trigger: the walk stops at whitespace, and a token starts with
    // exactly one of them.
    let hashPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (val[i] === '#') {
        if (i === 0 || /\s/.test(val[i - 1])) {
          hashPos = i;
        }
        break;
      }
      if (/\s/.test(val[i])) break;
    }
    if (hashPos >= 0) {
      setHashQuery(val.slice(hashPos + 1, cursorPos));
      setHashStartPos(hashPos);
      setShowHashDropdown(true);
      setSelectedHashIdx(0);
    } else {
      setShowHashDropdown(false);
    }
  }, []);

  // Insert selected shelf item handle into message
  const insertHashItem = useCallback((handle) => {
    const before = message.slice(0, hashStartPos);
    const after = message.slice(hashStartPos + 1 + hashQuery.length);
    const newMessage = `${before}#${handle}${after || ' '}`;
    setMessage(newMessage);
    setShowHashDropdown(false);
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + 1 + handle.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [message, hashStartPos, hashQuery]);

  // Insert selected mention into message
  const insertMention = useCallback((playerName) => {
    const before = message.slice(0, mentionStartPos);
    const after = message.slice(mentionStartPos + 1 + mentionQuery.length);
    const newMessage = `${before}@${playerName}${after ? after : ' '}`;
    setMessage(newMessage);
    setShowMentionDropdown(false);
    // Refocus textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + 1 + playerName.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [message, mentionStartPos, mentionQuery]);

  // Extract @mentions from message text and return target_players array
  const extractTargetPlayers = useCallback((msg) => {
    if (!players.length) return [];
    const mentioned = [];
    // Sort by name length descending so longer names match first
    const sortedPlayers = [...players].sort((a, b) => b.name.length - a.name.length);
    for (const p of sortedPlayers) {
      // Match @PlayerName (case-insensitive) with word boundary or end of string
      const regex = new RegExp(`@${p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$|[.,!?])`, 'i');
      if (regex.test(msg) && !mentioned.includes(p.name)) {
        mentioned.push(p.name);
      }
    }
    return mentioned;
  }, [players]);

  // --- Slash command palette (non-competitive "direct the space") ---
  const canUseCommands = alwaysAvailable && !competitiveMode && !imposterMode && !!onSubmitInterjection;
  const [selectedCommandIdx, setSelectedCommandIdx] = useState(0);
  // Palette visibility is derived, not stored: open while the draft is just
  // "/partial-command-name" (no whitespace yet). Typing a space or clearing
  // the box closes it — no state to desync.
  const commandQuery = message.startsWith('/') && !/\s/.test(message.slice(1)) ? message.slice(1).toLowerCase() : null;
  // composerFocused: suggestions are an ACTIVE-typing affordance — a
  // persisted draft alone must not summon them (focus-gating block above).
  const showCommandPalette = canUseCommands && composerFocused && commandQuery !== null;
  // 🚪 /room exists only where its answers can render: the desktop house
  // composer passes houseRooms; everywhere else (mobile dock, non-house
  // spaces) the command hides from the palette AND the parser, so typing it
  // gets the honest unknown-command line instead of a silent no-op (the
  // toast + chips are three-column-house-only — 09-15 preship review).
  const roomCommandLive = !!houseRooms;
  const portedOk = (c) => !(c.notPorted && portedRoom);
  const purposeOk = (c) => commandFitsPurpose(c.name, roomPurpose); // 🧭 10-02
  const filteredCommands = showCommandPalette
    ? hereFirst(SLASH_COMMANDS.filter(c => c.name.startsWith(commandQuery) && (!c.modOnly || isSpaceMod) && portedOk(c) && purposeOk(c) && placeOk(c, placeRoom, isMobile, placeOwner)
        && !(c.houseOnly && !roomCommandLive)
        && !(c.people && !houseRooms?.people) // 👥 10-01 people
        // 🕯️ room verbs: only in THEIR room, only while the backend lists them
        && roomVerbHere(c, houseRooms)
        && outingHere(c, houseRooms)
        && taleHere(c, houseRooms) // 🔥 10-03: /tale only while the tin is open
        && planHere(c, houseRooms))) // 📋 10-14: /plan only while house.plan says so
    : [];
  // 🌿 09-25 (Nina: option D): in a room, the things you can only do HERE lead
  // the list under their own header (the filter above already dropped every
  // other room's verbs, so a room/outing spec that survived belongs here).
  const hereCount = filteredCommands.filter(isHereCommand).length;
  const hereLabel = (houseRooms?.door?.label || '').trim();
  const clampedCommandIdx = Math.min(selectedCommandIdx, Math.max(0, filteredCommands.length - 1));
  // 🌿 09-25: the desktop palette is capped (a bare "/" listed ~40 commands in
  // a 1665px column whose top sat 900px above the screen, measured on the real
  // page, so this room's group at the TOP was never seen). The arrow keys keep
  // their row in view by moving the list's own scrollTop: scrollIntoView from
  // a fixed ancestor scrolls the document (the 08-01 lesson below).
  const paletteRef = useRef(null);
  useEffect(() => {
    const list = paletteRef.current;
    if (!list || !showCommandPalette) return;
    const row = list.querySelector(`[data-palette-idx="${clampedCommandIdx}"]`);
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (clampedCommandIdx === 0) list.scrollTop = 0;
    else if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [clampedCommandIdx, showCommandPalette]);

  // 💡 "Try this" row (Nina 07-26): one concrete PLAY per palette open, shown
  // under the command list while the query is still bare "/". Tapping it
  // fills the draft. Sparks beat descriptions — the rows above already
  // explain; this one inspires. Every command in the pool is public + real.
  const TRY_THIS_POOL = useMemo(() => ([
    '/scene sudden blackout',
    '/imagine this room as a renaissance painting',
    '/ship the two quietest locals',
    '/tinker a fortune cookie machine',
    '/translate pirate',
    '/drama over whose playlist is better',
    '/watch deep sea creatures',
    '/build a suspicious vending machine',
    // 🧭 10-02: a study or research room keeps only the plays that fit it
  ].filter((t) => commandFitsPurpose(t.slice(1).split(' ')[0], roomPurpose))), [roomPurpose]);
  const [paletteTip, setPaletteTip] = useState(null);
  useEffect(() => {
    if (showCommandPalette) {
      setPaletteTip(TRY_THIS_POOL[Math.floor(Math.random() * TRY_THIS_POOL.length)]);
      // Tour hook (08-06): the actions beat completes when the player's own
      // "/" summons the palette — rising edge only.
      onPaletteOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCommandPalette]);

  // Insert a command stub ("/ship ") into the box and focus it. Empty name
  // (⚡ button) inserts a bare "/" — the trailing space would close the
  // palette before it ever opened.
  const insertCommand = useCallback((name) => {
    const stub = name ? `/${name} ` : '/';
    setMessage(stub);
    setSelectedCommandIdx(0);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(stub.length, stub.length);
      }
    }, 0);
  }, []);

  // Parse a "/command @a @b free text" draft into { command } or { parseError }.
  // Target names resolve via the same extractor as @mentions; free text is the
  // draft minus the command word and the @mentions. Backend re-validates all of it.
  const parseSlashCommand = useCallback((msg) => {
    const trimmed = msg.trim();
    const cmdName = (trimmed.split(/\s+/)[0] || '').slice(1).toLowerCase();
    // 🥚 /egg is deliberately NOT in the registry (no palette, no autocomplete,
    // no card) — knowing a magic word IS the feature. Server resolves it.
    if (cmdName === 'egg') {
      const word = trimmed.slice(4).trim();
      if (!word) return { parseError: 'Whisper a magic word: /egg ______' };
      return { command: { name: 'egg', targets: [], text: word, card_message: null } };
    }
    // Aliases resolve here so a typed old name (/jukebox) still works — and
    // everything downstream (errors, hints, the submitted payload) speaks the
    // entry's real name, which quietly teaches the new one.
    // 🏡 placeOk INSIDE the find: two entries are named 'watch' (the place's
    // client-only one, the card-posting one), and find returns the first match.
    let spec = SLASH_COMMANDS.find(c => (c.name === cmdName || (c.aliases || []).includes(cmdName)) && placeOk(c, placeRoom, isMobile, placeOwner));
    // /room is unknown where it cannot answer (see roomCommandLive above) —
    // the backend would swallow it privately, but this surface could never
    // show that reply, so the parse error IS the feedback.
    if (spec?.houseOnly && !houseRooms) spec = null;
    if (spec?.people && !houseRooms?.people) spec = null; // 👥 10-01 people
    // 🕯️ A room verb the backend has not listed (flag dark, older backend) is
    // unknown. A LIVE one typed in the wrong room still goes through: the
    // server's private "You'd have to be in…" line is the feedback.
    if (spec && !roomVerbKnown(spec, houseRooms)) spec = null;
    // 🍎 An outing command is unknown unless the house is out there and you are in it.
    if (spec && !outingHere(spec, houseRooms)) spec = null;
    if (spec && !taleHere(spec, houseRooms)) spec = null; // 🔥 10-03 /tale: unknown unless the tin is open
    if (spec && !planHere(spec, houseRooms)) spec = null; // 📋 10-14 /plan: unknown while house.plan is absent
    if (spec && spec.notPorted && portedRoom) spec = null;
    if (spec && !commandFitsPurpose(spec.name, roomPurpose)) spec = null; // 🧭 10-02 not in this room
    if (!spec) {
      return { parseError: `Unknown command "/${cmdName}". Try: ${SLASH_COMMANDS.filter(c => (!c.modOnly || isSpaceMod) && !(c.notPorted && portedRoom) && commandFitsPurpose(c.name, roomPurpose) && placeOk(c, placeRoom, isMobile, placeOwner) && !(c.houseOnly && !houseRooms) && !(c.people && !houseRooms?.people) /* 👥 10-01 people */ && roomVerbHere(c, houseRooms) && outingHere(c, houseRooms) && taleHere(c, houseRooms) && planHere(c, houseRooms) /* 📋 10-14 */).map(c => '/' + c.name).join(', ')}` };
    }
    if (spec.modOnly && !isSpaceMod) {
      return { parseError: `/${spec.name} is mod-only. Only the space creator can use it!` };
    }
    // 🕯️ Room verbs take NO typed targets (the server picks whoever is in the
    // room), so an "@Fen" inside "/toast to @Fen's wiring" stays in the words
    // instead of being lifted out of them.
    const targets = ((spec.rooms && !spec.takesTarget) || spec.noTargets) ? [] : extractTargetPlayers(trimmed);
    if (targets.length < spec.minTargets) {
      return { parseError: `/${spec.name} needs ${spec.minTargets === 1 ? 'a local' : `${spec.minTargets} locals`} — tag them with @ (${spec.emoji} /${spec.name} ${spec.argHint})` };
    }
    // Free text = everything after the command word, minus the @mentions
    let text = trimmed.slice(cmdName.length + 1);
    targets.forEach(name => {
      const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp(`@${esc}(?=\\s|$|[.,!?])`, 'i'), '');
    });
    text = text.replace(/\s+/g, ' ').trim();
    // "/talk @a @b about pizza" — the card copy re-adds the connector word
    if (spec.minTargets > 0) text = text.replace(/^(about|over|to|re:?)\s+/i, '');
    if (spec.requiresText && !text) {
      return { parseError: `/${spec.name} needs a premise. Try /${spec.name} ${spec.argHint}` };
    }
    return {
      command: {
        name: spec.name,
        targets,
        text: text || null,
        // Optimistic card only — backend builds the authoritative copy.
        // Content commands have no card: the artifact itself is the result.
        card_message: spec.card ? spec.card(playerName || 'Someone', targets, text) : null
      }
    };
  }, [extractTargetPlayers, playerName, isSpaceMod, houseRooms, portedRoom, placeRoom, placeOwner, isMobile, roomPurpose]);

  // Handle keydown for mention dropdown navigation
  const handleKeyDownWithMention = useCallback((e, fallbackSubmit) => {
    // Command palette navigation takes precedence while it's open
    if (showCommandPalette && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIdx(Math.min(clampedCommandIdx + 1, filteredCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIdx(Math.max(clampedCommandIdx - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertCommand(filteredCommands[clampedCommandIdx].name);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMessage('');
        return;
      }
    }
    if (showHashDropdown && filteredShelf.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedHashIdx(prev => Math.min(prev + 1, filteredShelf.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedHashIdx(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertHashItem(itemHandle(filteredShelf[selectedHashIdx]));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowHashDropdown(false);
        return;
      }
    }
    if (showMentionDropdown && filteredPlayers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIdx(prev => Math.min(prev + 1, filteredPlayers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIdx(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        // ⏎⏎→⏎ (Nina 08-06): a FULLY-typed name used to cost two Enters —
        // the first eaten completing the mention (the canonical full name is
        // what extractTargetPlayers matches on, so the expansion is
        // load-bearing), the second sending. When the player already typed
        // the whole first word (or full name) AND the mention closes out the
        // message, Enter completes AND sends: mentionAutoSend arms the
        // deferred-send effect below, which fires after the expanded text
        // commits (same shape as queuedSend). Tab keeps complete-only; a
        // mid-message completion keeps complete-only (they're still writing).
        const pick = filteredPlayers[selectedMentionIdx];
        const q = mentionQuery.trim().toLowerCase();
        const name = (pick?.name || '').trim();
        const fullyTyped = q.length >= 2
          && (q === name.toLowerCase() || q === name.toLowerCase().split(/[\s,]+/)[0]);
        const mentionEndsMessage = mentionStartPos + 1 + mentionQuery.length >= message.trimEnd().length;
        insertMention(name);
        if (e.key === 'Enter' && fullyTyped && mentionEndsMessage
          && alwaysAvailable && onSubmitInterjection && !isYourTurn) {
          setMentionAutoSend(true);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }
    // Default: Enter to submit (not shift+enter). An Enter that confirms an
    // IME candidate (Japanese / Chinese / Korean) is not a send: it used to
    // send the half-composed line (CouchColumn's guard; 🏡 on the place page
    // Enter is the only way to send).
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing) && e.keyCode !== 229) {
      e.preventDefault();
      fallbackSubmit(e);
    }
  }, [showMentionDropdown, filteredPlayers, selectedMentionIdx, insertMention,
      showCommandPalette, filteredCommands, clampedCommandIdx, insertCommand,
      showHashDropdown, filteredShelf, selectedHashIdx, insertHashItem,
      mentionQuery, mentionStartPos, message, alwaysAvailable, onSubmitInterjection, isYourTurn]);

  // Shelf (#) dropdown — same slot as the mention dropdown
  const renderHashDropdown = () => {
    if (!showHashDropdown || !composerFocused || filteredShelf.length === 0) return null;
    return (
      <div data-tour-popover="" style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        marginBottom: '4px',
        backgroundColor: 'rgba(30, 30, 40, 0.98)',
        border: '1px solid rgba(190, 140, 255, 0.35)',
        borderRadius: '8px',
        maxHeight: '180px',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
        ...popoverMobileFlow,
      }}>
        <div style={{ padding: '6px 12px', fontSize: '0.7em', fontWeight: 700, color: 'rgba(190,140,255,0.85)', borderBottom: '1px solid rgba(255,255,255,0.06)', letterSpacing: '0.05em' }}>
          🗄️ ON THE SHELF
        </div>
        {filteredShelf.map((it, idx) => (
          <div
            key={itemHandle(it)}
            onMouseDown={(e) => { e.preventDefault(); insertHashItem(itemHandle(it)); }}
            onMouseEnter={() => setSelectedHashIdx(idx)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: idx === selectedHashIdx ? 'rgba(190, 140, 255, 0.18)' : 'transparent',
              display: 'flex', alignItems: 'center', gap: '8px',
              borderBottom: idx < filteredShelf.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
          >
            <span style={{ fontSize: '1em' }}>{it.emoji || '📦'}</span>
            <div>
              <div style={{ color: '#fff', fontSize: '0.9em', fontWeight: 600 }}>#{itemHandle(it)}</div>
              {it.built_by && (
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7em' }}>{it.via_reward === 'resident_gift' ? 'from' : 'built by'} {it.built_by}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 📱 Popovers join the FLOW on phones (Nina 08-06 "it keeps cutting off"):
  // the thumb dock is maxHeight:50dvh + overflowY:auto, and an absolute
  // bottom:100% popover paints ABOVE the dock's box — ancestor overflow
  // clips it and scroll can never reach upward content, so the palette
  // showed only its last row. No z-index can beat overflow clipping; in-flow
  // (first in the wrap row, own internal scroll) keeps everything inside the
  // dock and reachable. Desktop keeps the pop-over-the-composer look.
  const popoverMobileFlow = isMobile
    ? { position: 'static', flex: '0 0 100%', maxHeight: '34dvh', overflowY: 'auto' }
    : null;

  // Command palette JSX — sits where the mention dropdown sits (they're
  // mutually exclusive: "/" only opens before any whitespace, "@" needs text)
  const renderCommandPalette = () => {
    if (!showCommandPalette || filteredCommands.length === 0) return null;
    return (
      <div data-tour-popover="" data-command-palette="1" ref={paletteRef} style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        marginBottom: '4px',
        backgroundColor: 'rgba(30, 30, 40, 0.98)',
        border: '1px solid rgba(255, 200, 100, 0.35)',
        borderRadius: '8px',
        overflowX: 'hidden', // never the `overflow` shorthand beside overflowY (React's conflicting-style warning)
        // desktop cap (phones keep popoverMobileFlow's 34dvh below)
        maxHeight: 'min(52vh, 440px)',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
        ...popoverMobileFlow,
      }}>
        <div style={{ padding: '6px 12px', fontSize: '0.7em', fontWeight: 700, color: 'rgba(255,200,100,0.8)', borderBottom: '1px solid rgba(255,255,255,0.06)', letterSpacing: '0.05em' }}>
          {hereCount > 0 ? `${houseRooms?.door?.icon || '🚪'} HERE IN ${(hereLabel || 'this room').toUpperCase()}` : '⚡ ACTIONS'}
        </div>
        {filteredCommands.map((c, idx) => (
          <React.Fragment key={c.name}>
          {hereCount > 0 && idx === hereCount && (
            <div data-palette-group="anywhere" style={{ padding: '6px 12px', fontSize: '0.7em', fontWeight: 700, color: 'rgba(255,255,255,0.45)', borderTop: '1px solid rgba(255,200,100,0.2)', borderBottom: '1px solid rgba(255,255,255,0.06)', letterSpacing: '0.05em' }}>
              ⚡ ANYWHERE
            </div>
          )}
          <div
            data-palette-idx={idx}
            data-palette-here={idx < hereCount ? '1' : undefined}
            onMouseDown={(e) => { e.preventDefault(); insertCommand(c.name); }}
            onMouseEnter={() => setSelectedCommandIdx(idx)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: idx === clampedCommandIdx ? 'rgba(255, 200, 100, 0.15)' : 'transparent',
              borderBottom: idx < filteredCommands.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ color: '#fff', fontSize: '0.9em', fontWeight: 700 }}>{c.emoji} /{c.name}</span>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75em' }}>{c.argHint}</span>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75em', marginTop: '2px' }}>{c.description}</div>
          </div>
          </React.Fragment>
        ))}
        {/* 💡 Try-this footer — bare "/" only, so it never fights the filter */}
        {commandQuery === '' && paletteTip && (
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              setMessage(paletteTip + ' ');
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.focus();
                  const len = textareaRef.current.value.length;
                  textareaRef.current.setSelectionRange(len, len);
                }
              }, 0);
            }}
            style={{
              padding: '7px 12px', cursor: 'pointer',
              borderTop: '1px solid rgba(255,200,100,0.2)',
              backgroundColor: 'rgba(255,200,100,0.06)',
              fontSize: '0.75em', color: 'rgba(255,200,100,0.85)',
            }}
          >
            💡 Try: <span style={{ fontWeight: 700, color: '#fff' }}>{paletteTip}</span>
          </div>
        )}
      </div>
    );
  };

  // Post-pick arg hint bar (07-13): once a command word is committed
  // ("/ship "), the palette closes — this bar takes the same slot and teaches
  // the argument shape, with tappable @local / #item chips that fill the
  // draft. Derived like the palette (no state to desync); yields to the
  // @ / # dropdowns while they're open.
  const activeCmdSpec = (canUseCommands && composerFocused && /^\/[a-z][a-z-]*\s/i.test(message)) // hyphens: /tell-the-tomatoes
    ? SLASH_COMMANDS.find(c => {
        const w = message.slice(1).split(/\s/)[0].toLowerCase();
        if ((w === 'room' || w === 'look') && !houseRooms) return false; // dead surface — no bar
        if (!roomVerbKnown(c, houseRooms)) return false; // 🕯️ a room verb that is not live here has no bar either
        if (!outingHere(c, houseRooms)) return false; // 🍎 same for an outing command
        if (!taleHere(c, houseRooms)) return false; // 🔥 10-03 /tale: no bar unless the tin is open
        if (!planHere(c, houseRooms)) return false; // 📋 10-14 /plan: no bar while house.plan is absent
        if (!portedOk(c)) return false; // 🏡 The Terminal's /task, in a ported room
        if (!purposeOk(c)) return false; // 🧭 10-02 a verb this room doesn't keep has no bar
        if (!placeOk(c, placeRoom, isMobile, placeOwner)) return false; // 🏡 W2d: the place's /watch, never the card one (and vice versa)
        if (c.people && !houseRooms?.people) return false; // 👥 10-01 people
        return c.name === w || (c.aliases || []).includes(w);
      }) || null
    : null;
  const appendDraftToken = useCallback((tok) => {
    setMessage(m => (m === '' || /\s$/.test(m) ? m : m + ' ') + tok + ' ');
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 0);
  }, []);
  const renderCommandArgHint = () => {
    if (!activeCmdSpec || showCommandPalette || showMentionDropdown || showHashDropdown) return null;
    const spec = activeCmdSpec;
    const parsedCmd = parseSlashCommand(message).command || null;
    const ready = !!parsedCmd;
    // Chips: locals for target-taking commands; shelf items for /build. Same
    // locals-only rule as the @ dropdown (humans get rejected server-side).
    const localChips = spec.minTargets > 0
      ? players.filter(p => p.name && !(p.is_human || p.model === 'Human Player')).slice(0, 4)
      : [];
    const shelfChips = (spec.name === 'build' || spec.name === 'gift') ? shelfItems.slice(0, 4) : [];
    // 🚪🖼️ /room LIST (09-18, mockup v12 — replaces the 09-15 chips): one row
    // per OPEN room (thumb · name · text mark · faces · watchers), built by
    // the parent from utils/roomView.roomPickerRows so this list and the
    // pictures popup can never disagree. Tap a row = GO — it SENDS through
    // the parent's one mover (the same private path as typing it), it does
    // not fill the draft. No locked/undiscovered room ever rides here. The
    // rows do NOT filter on what is typed (visibility keyed on live typing
    // eats focus and clicks — the 08-20 import-wizard lesson).
    const roomRows = (spec.name === 'room' && houseRooms && Array.isArray(houseRooms.rows)) ? houseRooms.rows : [];
    const goRoomRow = (id) => {
      if (typeof houseRooms?.onGo !== 'function') { setMessage(`/room ${id}`); return; }
      setMessage('');
      setError(null);
      retireRoomHint();
      houseRooms.onGo(id);
    };
    const chipStyle = {
      border: '1px solid rgba(255,200,100,0.4)', background: 'rgba(255,200,100,0.08)',
      color: '#ffd98a', borderRadius: 999, padding: '2px 9px', fontSize: '0.72em',
      cursor: 'pointer', whiteSpace: 'nowrap',
    };
    return (
      <div data-tour-popover="" style={{
        position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: '4px',
        backgroundColor: 'rgba(30, 30, 40, 0.98)', border: '1px solid rgba(255, 200, 100, 0.35)',
        borderRadius: '8px', zIndex: 1000, boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
        padding: '7px 12px', display: 'flex', flexDirection: 'column', gap: 6,
        // Narrow phones (Nina 08-04): the bar can never poke past its slot —
        // border-box + hidden guard against any child that refuses to shrink.
        boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden',
        ...popoverMobileFlow,
        // the bar is short — never needs the in-flow scroll cap
        ...(isMobile ? { maxHeight: 'none', overflowY: 'hidden' } : null),
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* minWidth:0 lets the syntax span actually SHRINK — flex items
              floor at min-content by default, so on narrow phones this span
              refused to give way and shoved the status label off the right
              edge (the "tag with @" clip in Nina's 08-04 screenshot). */}
          <span style={{ color: 'rgba(255,200,100,0.85)', fontSize: '0.75em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '1 1 auto' }}>
            {spec.emoji} /{spec.name} <span style={{ opacity: 0.7, fontWeight: 400 }}>{spec.argHint}</span>
          </span>
          <span style={{
            marginLeft: 'auto', fontSize: '0.68em', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0,
            color: ready ? '#7CFC9B' : 'rgba(255,255,255,0.45)',
          }}>
            {/* /room: the promise must match what send DOES — a bare draft
                lists, a filled draft moves (09-15 preship review). */}
            {ready
              ? (spec.name === 'room'
                ? (parsedCmd.text ? '✓ send to head there' : '✓ tap a room, or send for pictures')
                : (placeRoom && !isMobile ? '✓ Ready! Press Enter' : '✓ Ready! Hit send'))
              : (spec.minTargets > 0 ? 'tag with @' : 'add your idea')}
          </span>
        </div>
        {/* 💡 What a ROOM VERB does (Nina 09-19: "no idea what /marginalia does").
            A plate chip seeds "/marginalia " with a trailing space, which opens
            THIS bar instead of the palette, and the palette was the only place
            the sentence lived. Also the touch door: a phone has no hover for
            the chip's tooltip. Room verbs only; every other bar is unchanged.
            Wraps (never ellipsis): the words are the point. */}
        {roomVerbBarLine(spec) && (
          <div data-testid="room-verb-hint" style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.74em', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
            {roomVerbBarLine(spec)}
          </div>
        )}
        {/* 💡 E2 (Nina 09-26): a ported room's pictures today, on the picture
            verbs only ("2 left today"; at zero the lights copy). The parent
            passes `pictures` for ported rooms alone, so every other space is
            byte-identical here. */}
        {picturesHint(pictures, spec.name) && (
          <div data-testid="pictures-hint" style={{ color: 'rgba(207,248,252,0.85)', fontSize: '0.74em', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
            {picturesHint(pictures, spec.name)}
          </div>
        )}
        {roomRows.length > 0 && (
          <RoomPickerList rows={roomRows} onGo={goRoomRow} compact={isMobile} />
        )}
        {(localChips.length > 0 || shelfChips.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {localChips.map(p => (
              <button key={p.name} type="button" style={chipStyle}
                onMouseDown={(e) => { e.preventDefault(); appendDraftToken(`@${p.name}`); }}>
                @{p.name}
              </button>
            ))}
            {shelfChips.map(it => (
              <button key={itemHandle(it)} type="button" style={{ ...chipStyle, borderColor: 'rgba(190,140,255,0.4)', background: 'rgba(190,140,255,0.08)', color: '#d9baff' }}
                onMouseDown={(e) => { e.preventDefault(); appendDraftToken(`#${itemHandle(it)}`); }}>
                {it.emoji || '📦'} #{itemHandle(it)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Mention dropdown JSX (reusable across modes)
  const renderMentionDropdown = () => {
    if (!showMentionDropdown || !composerFocused || filteredPlayers.length === 0) return null;
    return (
      <div
        ref={mentionDropdownRef}
        data-tour-popover=""
        style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          marginBottom: '4px',
          backgroundColor: 'rgba(30, 30, 40, 0.98)',
          border: '1px solid rgba(100, 180, 255, 0.3)',
          borderRadius: '8px',
          maxHeight: '180px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
          ...popoverMobileFlow,
        }}
      >
        {filteredPlayers.slice(0, 6).map((p, idx) => (
          <div
            key={p.game_player_id || p.name}
            onMouseDown={(e) => { e.preventDefault(); insertMention(p.name); }}
            onMouseEnter={() => setSelectedMentionIdx(idx)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: idx === selectedMentionIdx ? 'rgba(100, 180, 255, 0.2)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: idx < filteredPlayers.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
          >
            {p.profile_image_url ? (
              <img src={p.profile_image_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: 'rgba(100,180,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7em', color: '#fff', fontWeight: 700 }}>
                {p.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ color: '#fff', fontSize: '0.9em', fontWeight: 600 }}>{p.name}</div>
              {/* 🏡 wave 2b: a place page prints names only (never a model id) */}
              {p.model && p.model !== 'Human Player' && !placeRoom && (
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7em' }}>{p.model}</div>
              )}
              {p.isCrowd && (
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7em' }}>in the crowd</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Reset turnSubmitted when it's no longer your turn (turn was processed)
  useEffect(() => {
    if (!isYourTurn) {
      setTurnSubmitted(false);
    }
  }, [isYourTurn]);

  // 🎯 Docket dare reveal (Nina 07-30: the composer's first glance should be
  // ASK + INPUT and nothing else). The dare stays hidden until the first real
  // keystroke — you always type before you send, so it still lands at the
  // composing moment. Keyed on typing, NOT focus: the textarea autoFocuses on
  // mount, so a focus trigger would reveal instantly and change nothing.
  const [docketDareSeen, setDocketDareSeen] = useState(false);
  useEffect(() => { if (!isYourTurn) setDocketDareSeen(false); }, [isYourTurn]);

  // Pre-fill the opening line so the human continues from where everyone
  // starts — and (Locals v2, Nina 08-01) re-seed when the VALUE changes, so
  // cast-card verbs (@Name, /gift @Name) land in an empty box. The ref holds
  // the last-applied string: a given seed still applies exactly once, and
  // nothing the player typed is ever overwritten.
  const prefilledRef = useRef(null);
  const prefillNonceRef = useRef(prefillNonce);
  useEffect(() => {
    const nonceBumped = prefillNonceRef.current !== prefillNonce;
    prefillNonceRef.current = prefillNonce;
    if (prefillText && (prefilledRef.current !== prefillText || nonceBumped) && !message.trim()) {
      setMessage(prefillText);
      prefilledRef.current = prefillText;
    } else if (prefilledRef.current && prefillText !== prefilledRef.current && message === prefilledRef.current) {
      // The seed we injected was retracted or replaced while the player left
      // it untouched (tutorial skip/end) — take it back with it. Player-typed
      // text never matches the applied seed exactly, so it's never clobbered.
      setMessage(prefillText || '');
      prefilledRef.current = prefillText || null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillText, prefillNonce]);

  // Get available action details
  const getActionDetails = (actionName) => {
    return availableActions.find(a => a.action === actionName) || {};
  };

  // Calculate current action cost
  const currentActionCost = getActionDetails(actionType).cost_tokens || 0;

  // Check if can afford action
  const canAfford = tokensLeft >= currentActionCost;

  // Get list of possible recipients (for private/secret messages)
  const possibleRecipients = (() => {
    const action = getActionDetails(actionType);
    if (action.format && action.format.recipients) {
      return Array.isArray(action.format.recipients) ? action.format.recipients : [];
    }
    return [];
  })();

  // Validation
  const validate = () => {
    setError(null);

    // Check tokens
    if (!canAfford) {
      setError(`Not enough credits! This action costs ${currentActionCost} but you have ${tokensLeft}`);
      return false;
    }

    // Check message for non-pass actions
    if (actionType !== 'pass' && !message.trim()) {
      setError('Please enter a message');
      return false;
    }

    // Check recipients for private/secret messages
    if ((actionType === 'private_message' || actionType === 'secret_message') && recipients.length === 0) {
      setError('Please select at least one recipient');
      return false;
    }

    // Dice Power Word Ban (fumble): the human may not use the banned word this turn.
    if (actionType !== 'pass') {
      const banErr = bannedWordError(message);
      if (banErr) { setError(banErr); return false; }
    }

    return true;
  };

  // Word Ban v2 — the viewer's banned words this round (targeted casts and/or the fumble
  // Lexicon Curse). Deduped list drives the loud banner, the LIVE draft check, and the
  // submit block (all three submit paths call bannedWordError).
  const banEntries = Array.isArray(bannedWords) ? bannedWords.filter(b => b && b.word) : [];
  const banWordsList = [...new Set(banEntries.map(b => b.word))];
  const isLexiconCurse = banEntries.some(b => b.source === 'fumble');

  // Which banned words the text uses (whole-word, case-insensitive). [] when clean/no bans.
  const violatingWords = (text) => {
    if (!banWordsList.length || !text) return [];
    return banWordsList.filter(w => {
      const esc = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`\\b${esc}\\b`, 'i').test(text);
    });
  };

  // Returns an error string if the text uses any banned word, else null. Used by the turn
  // submit, the competitive submit, and the free-chat interjection.
  const bannedWordError = (text) => {
    const hits = violatingWords(text);
    if (!hits.length) return null;
    return `🚫 ${hits.map(w => `“${w}”`).join(', ')} ${hits.length > 1 ? 'are' : 'is'} banned this turn. Rephrase.`;
  };

  // LOUD banned-words banner (Word Ban v2): always visible while a ban is active, with the
  // full banned list as struck-through chips and a LIVE draft check — chips light up red the
  // moment the draft uses them, so a blocked send is never a mystery (the old single-quiet-chip
  // treatment read as "the send button is broken"). Fumble (Lexicon Curse) gets the full-dread
  // variant. Rendered above the error slot in every mode that can type.
  const renderBanNotice = () => {
    if (!banWordsList.length) return null;
    const hits = violatingWords(message);
    const RED = '#ff5252';
    const c = isLexiconCurse ? RED : '#ff9800';
    return (
      <div style={{ marginBottom: '8px', padding: '10px 12px', borderRadius: '10px', border: `2px solid ${c}`, backgroundColor: `${c}1a` }}>
        <div style={{ fontSize: '0.85em', fontWeight: 800, color: c, marginBottom: '6px' }}>
          {isLexiconCurse
            ? '💀 LEXICON CURSE. Your fumble banned every word below. Talk around them:'
            : `🚫 A rival banned ${banWordsList.length > 1 ? 'words' : 'a word'} for you this round. Who? Revealed at round end:`}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {banWordsList.map(w => {
            const hit = hits.includes(w);
            return (
              <span key={w} style={{
                fontSize: '0.8em', fontWeight: 700, padding: '2px 8px', borderRadius: '6px',
                border: `1px solid ${hit ? RED : c}`, color: hit ? '#fff' : c,
                backgroundColor: hit ? RED : 'transparent', textDecoration: 'line-through',
              }}>{w}</span>
            );
          })}
        </div>
        {hits.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '0.78em', fontWeight: 700, color: RED }}>
            ✋ Banned {hits.length > 1 ? 'words' : 'word'} in your draft: {hits.map(w => `“${w}”`).join(', ')}. Rephrase.
          </div>
        )}
      </div>
    );
  };

  // 🔒 In-flight lock for free-chat sends (09-20, Nina: "my message is sent
  // twice"). `isSubmitting` is STATE: it only disables the Send BUTTON, and
  // only after a render. The Enter path had no guard at all, and the text sat
  // in the box for the whole round trip (POST + the awaited status refresh),
  // so it read as unsent and a second Enter posted a second real row (fresh
  // client_msg_id each time, so no dedupe could ever catch it). A ref is
  // synchronous: the second Enter dies here.
  const interjectionInFlightRef = useRef(false);

  // Handle interjection submit (free chat mode)
  const handleInterjectionSubmit = async (e) => {
    e.preventDefault();
    if (interjectionInFlightRef.current) return;

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    const banErr = bannedWordError(message);
    if (banErr) { setError(banErr); return; }

    // ⏱ Cooldown active: the chip already says when. Enter/queued sends land
    // here past the button's disabled, and firing into a known-closed window
    // just re-429s and restarts the countdown longer than it was.
    if (rateLimitedUntil && Date.now() < rateLimitedUntil) return;

    // Parent lock on (AI turn in flight): queue instead of submitting into the
    // lock. Message stays in the box; the unlock effect sends it.
    if (disabled) {
      setQueuedSend(true);
      setError(null);
      return;
    }

    // Slash command: parse client-side so typos never post to the room.
    // Parse errors render in the private error slot only.
    let command = null;
    if (canUseCommands && message.trim().startsWith('/')) {
      const parsed = parseSlashCommand(message);
      if (parsed.parseError) { setError(parsed.parseError); return; }
      command = parsed.command;
    }

    interjectionInFlightRef.current = true;
    setIsSubmitting(true);
    setError(null);

    // The box empties the moment the send starts (the optimistic bubble is
    // already in the feed), and the text comes back ONLY if the send fails
    // and the box is still empty (never over something typed meanwhile).
    const sentText = message;
    setMessage('');
    // …and so does the draft store, NOW: a send that unmounts this composer in
    // the same batch (🏡 /story opens a lane with no composer) never runs the
    // draft-mirror effect, and the next mount would restore the sent text.
    composerDrafts.set(playerId || 'anon', '');
    setShowMentionDropdown(false);

    try {
      const targetPlayers = extractTargetPlayers(sentText.trim());
      await onSubmitInterjection(sentText.trim(), targetPlayers, command);
      if (command?.name === 'room') retireRoomHint(); // 🆕 they found it
    } catch (err) {
      setMessage((cur) => (cur ? cur : sentText));
      // ⏱ 429 → ticking chip instead of a static error (the chip owns the
      // copy; a stale "try again later" under a live countdown reads broken)
      if (err && err.retryAfterSec > 0) {
        setRateLimitedUntil(Date.now() + err.retryAfterSec * 1000);
        setError(null);
      } else {
        setError(err.message || 'Failed to submit message');
      }
    } finally {
      interjectionInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Fire a queued free-chat send the moment the parent lock releases. Guards
  // re-check everything at fire time (text may have been edited or cleared,
  // the round's message budget may have run out). Reads current state because
  // the effect closure is recreated every render.
  useEffect(() => {
    if (disabled || !queuedSend) return;
    setQueuedSend(false);
    if (message.trim() && !isSubmitting && interjectionsRemaining > 0) {
      handleInterjectionSubmit({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, queuedSend]);

  // ⏎⏎→⏎ deferred half of the one-Enter mention send: fires AFTER the render
  // that committed the expanded mention text, so the submit reads the full
  // "@Sonnet Five, Esq." message, not the pre-expansion draft. Guards
  // re-check at fire time, same as queuedSend above.
  useEffect(() => {
    if (!mentionAutoSend) return;
    setMentionAutoSend(false);
    if (message.trim() && !isSubmitting && interjectionsRemaining > 0) {
      // Parent lock on (AI turn in flight) → hand off to queuedSend so the
      // message fires on release instead of dying silently.
      if (disabled) setQueuedSend(true);
      else handleInterjectionSubmit({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionAutoSend]);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If not your turn, use interjection handler
    if (!isYourTurn && alwaysAvailable && onSubmitInterjection) {
      return handleInterjectionSubmit(e);
    }

    // Slash commands always go the interjection route, even on your turn —
    // otherwise "/ship @a @b" would post into chat as a raw turn message.
    if (canUseCommands && message.trim().startsWith('/')) {
      return handleInterjectionSubmit(e);
    }

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const action = {
        action: actionType,
        message: actionType === 'pass' ? '' : message.trim(),
        thoughts: thoughts.trim(),
      };

      // Add recipients for private/secret messages
      if (actionType === 'private_message' || actionType === 'secret_message') {
        action.recipients = recipients;
      }

      // Extract @mentions as target_players
      if (actionType === 'public_message') {
        const targetPlayers = extractTargetPlayers(action.message);
        if (targetPlayers.length > 0) {
          action.target_players = targetPlayers;
        }
      }

      await onSubmit(action);

      // Clear form on success
      setMessage('');
      setThoughts('');
      setRecipients([]);
      setActionType('public_message');
      setShowMentionDropdown(false);
      if (isYourTurn) setTurnSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit action');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (recipient) => {
    if (recipients.includes(recipient)) {
      setRecipients(recipients.filter(r => r !== recipient));
    } else {
      setRecipients([...recipients, recipient]);
    }
  };

  // Reset recipients when action type changes
  useEffect(() => {
    setRecipients([]);
  }, [actionType]);

  // Theme defaults
  const THEME = {
    primary: theme.primary || '#2196F3',
    secondary: theme.secondary || '#4CAF50',
    accent: theme.accent || '#FFC107',
    bgLight: theme.bgLight || '#FAFAFA',
    bgMedium: theme.bgMedium || '#F5F5F5',
    textDark: theme.textDark || '#212121',
    textMuted: theme.textMuted || '#757575',
    textLight: theme.textLight || '#FFFFFF',
    borderLight: theme.borderLight || 'rgba(0, 0, 0, 0.12)',
    error: theme.error || theme.evil || '#F44336',
    success: theme.success || theme.good || '#4CAF50',
    warning: theme.warning || theme.accent || '#FF9800'
  };
  // 🌙 W2c: an AI verb refused while everyone sleeps ("💤 Everyone’s asleep.
  // Try that when they wake.", the server's words) reads as the night, not an error.
  const errorTone = typeof error === 'string' && error.startsWith('💤') ? '#c2caf4' : THEME.error;

  // ⚖️ Vote owns the screen (Nina 07-30, approved mock): while the lineup wants
  // this viewer's ballot the composer gets out of the way. Idle states vanish
  // outright; a genuinely OPEN turn keeps its REAL countdown visible in one
  // quiet line — the server clock never pauses, so the line must not lie.
  if (voteFold && (imposterMode || competitiveMode)) {
    if (!isYourTurn || turnSubmitted) return null;
    return (
      <div style={{
        border: '1px dashed rgba(232,221,200,0.3)', borderRadius: '10px',
        padding: '8px 12px', fontSize: '0.8em', color: 'rgba(255,255,255,0.55)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span aria-hidden>✍️</span>
        <span style={{ flex: 1 }}>Vote first. Your statement window stays open.</span>
        {secondsLeft != null && (
          <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: secondsLeft <= 10 ? '#ff6666' : 'rgba(255,255,255,0.7)' }}>
            {secondsLeft}s
          </span>
        )}
      </div>
    );
  }

  // Imposter/competitive mode but not your turn: hide the free chat form entirely
  if ((imposterMode || competitiveMode) && !isYourTurn) {
    if (suppressIdle && !playRoundButton) return null; // roll card footer says it
    return (
      <div style={{
        backgroundColor: 'rgba(20,20,30,0.8)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.4)',
        fontSize: '0.9em',
        border: '1px solid rgba(100,180,255,0.15)',
      }}>
        {playRoundButton || 'Waiting for your turn...'}
      </div>
    );
  }

  // Simplified free chat mode (when not your turn)
  if (!isYourTurn && alwaysAvailable) {
    // `disabled` (parent lock, e.g. AI turn in flight) deliberately does NOT
    // disable composing or the Send button here — sending while locked queues
    // (see queuedSend). Silent dead clicks at the lock boundary were the 5g.25
    // send-drop bug.
    const rateWaitSec = rateLimitedUntil
      ? Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000)) : 0;
    const sendDisabled = isSubmitting || !message.trim() || interjectionsRemaining <= 0 || rateWaitSec > 0;
    const pokeDisabled = !onPoke || isPoking || isSubmitting || !!resting;
    // 🏡 W2d, mockup v9's composer (desktop place page): the box + 👋 + ✏️.
    // Enter sends (no Send button); typing / opens the actions (no / button);
    // upload stays, quietly, so nothing becomes unreachable.
    const placeDesk = placeRoom && !isMobile;
    const placeBtn = {
      width: '40px', height: '40px', padding: 0, flex: '0 0 auto', borderRadius: '10px',
      border: '1px solid rgba(240,172,85,0.35)', background: 'rgba(240,172,85,0.08)', color: '#F5C580',
      fontSize: '1.05em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
    // 🧱 the room frame's composer (09-30, mockup frame A; `framed` = only the
    // ONE composer the frame mounts): ONE box, [tools] [the line] [Share]
    // [Send], all in one row at every width. Phones fold the tools behind a ＋
    // (today's places-nav tray: the same tiles, the same handlers). Share IS
    // the upload there (its Doc · Image · Video cover the 📎's whole accept
    // list), so the 📎 steps aside. Every other mount: frameOn false, trayOn
    // = today's `trayMode && isMobile`, nothing below changes.
    const frameOn = !!framed;
    const trayOn = (trayMode && isMobile) || (frameOn && isMobile);
    const frameShareIsUpload = frameOn && !!share && !!onUploadFile;
    const frameTool = {
      width: isMobile ? '40px' : '36px', height: isMobile ? '40px' : '36px', padding: 0, flex: '0 0 auto',
      borderRadius: '10px', border: 'none', background: 'var(--rs-hover, #1A1E25)', color: 'var(--rs-ink, #ECEEF2)',
      lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    };
    // 🚪📅 09-20 (Nina, mockup option B): the doors no longer sit LEFT of the
    // box as two framed columns. Desktop: a quiet strip on the bottom edge
    // INSIDE the box. Phones: the box is one line tall, so they ride the tool
    // strip under it (order 1, far left) and the box takes the full row back.
    // Nothing sits left of the box any more, so the in-input overlays and the
    // wrapped-row width all start at 0.
    const hasDoors = !!(houseRooms?.door || houseCalendar?.door);
    const doorsInBox = hasDoors && !isMobile;
    const leftDoorsW = 0;
    // 🌿 09-25 (Nina: option B + D): in a room the old bar above the box is
    // gone. `floor` = { presence, note, band, verbs, onVerb, onLook } from the
    // page (null in the main room): the verbs ride the floor after the doors,
    // who is here reads in the empty box, the calendar folds to its leaf.
    // 🧭 10-01 composer (R12): the leaf fold is gone, the clock is back; the
    // chips sit at the RIGHT end of the floor (RoomVerbLane's inner run).
    const floor = houseRooms?.floor || null;
    const floorEmoji = (w) => {
      const c = SLASH_COMMANDS.find((x) => x.name === w || (x.aliases || []).includes(w));
      return c ? c.emoji : null;
    };
    const renderFloorLane = (compact) => (floor ? (
      <RoomVerbLane compact={compact} chips={floor.verbs || []} band={floor.band || null}
        onVerb={floor.onVerb} onLook={floor.onLook || null} emojiFor={floorEmoji} lit={floor.lit || null} />
    ) : null);
    const renderDoors = (compact) => (
      <>
        {houseRooms?.door && (
          <InlineRoomDoor
            compact={compact}
            icon={houseRooms.door.icon}
            label={houseRooms.door.label}
            walking={houseRooms.door.walking || null}
            open={!!houseRooms.door.open}
            onToggle={() => { retireRoomHint(); if (houseRooms.door.onToggle) houseRooms.door.onToggle(); }}
          />
        )}
        {houseRooms?.door && houseCalendar?.door && !compact && (
          <span aria-hidden="true" style={{ width: 1, height: 13, background: 'rgba(122,96,60,0.55)', flexShrink: 0 }} />
        )}
        {houseCalendar?.door && (
          <InlineCalendarDoor
            compact={compact}
            text={houseCalendar.door.text}
            day={houseCalendar.door.day}
            hot={!!houseCalendar.door.hot}
            title={houseCalendar.door.title}
            open={!!houseCalendar.door.open}
            onToggle={houseCalendar.door.onToggle}
            face={houseCalendar.door.face || null}
            /* 🧭 10-01 composer (R12): no leafOnly any more: the clock is back in every room, desktop and phone */
          />
        )}
      </>
    );
    // 🤔📥 Idle-composer thinking overlay (Nina 08-27): while the box is empty
    // AND unfocused, "X is thinking" renders INSIDE the input box (over the
    // placeholder); the moment the user engages (focus or a draft), it yields
    // back to the strip-row slot below. Both inputs flip only at discrete
    // change/focus/blur transitions (the sanctioned pattern — the textarea is
    // never unmounted and the overlay is pointer-transparent, so no eaten taps
    // and no dropped focus). interjectionsRemaining > 0 keeps the "No messages
    // left this round" placeholder visible instead of masking it.
    const thinkingOverlayOn = isMobile && !!thinkingStatus && !composerFocused
      && !message && interjectionsRemaining > 0;
    // The box's usual teach line (was inline on the textarea until 09-18; a
    // const now because the room hint shows it too, after its red lead-in).
    // Narrow container: the long teach strings wrap into clipped lines (Nina
    // 08-14 screenshot) — compact hints instead.
    const usualPlaceholder = interjectionsRemaining > 0
      ? (resting ? ASLEEP_COPY.placeholder : placeDesk && placeName ? `Say something to ${placeName}…` : frameOn ? (framed.name && !isMobile ? `Say something to ${framed.name}…` : 'Say something…') : narrowDesktop
          ? (canUseCommands ? "Say something... (@ mention, / actions)" : "Say something... (@ to mention)")
          : (watchPartyOn
              ? (canUseCommands ? "Say something... (@ to mention, / for actions, @watchparty for couch talk)" : "Say something... (@ to mention, @watchparty for couch talk)")
              : (canUseCommands ? "Say something... (@ to mention, / for actions)" : "Say something... (@ to mention)")))
      : (resting ? ASLEEP_COPY.notesDone : "No messages left this round");
    // 🆕🚪 The room hint rides the SAME sanctioned pattern as the thinking
    // overlay: it flips only on discrete change events (an empty draft), the
    // textarea is never unmounted, and the overlay is pointer-transparent, so
    // a tap always lands on the box. Thinking wins the slot; an exhausted
    // budget keeps its own "No messages left" line.
    const roomHintOn = inHouseSpace && roomHintAlive && canUseCommands && !message
      && interjectionsRemaining > 0 && !thinkingOverlayOn && !resting;
    // 🌿 who is here (09-25, option B): the old room bar's presence line reads
    // in the EMPTY box, same sanctioned pattern (flips on an empty draft, the
    // textarea never unmounts, the overlay never takes a tap). A presence
    // change ("Fen wanders in.") takes the slot for its minute. Thinking and
    // the red room hint win the slot. 09-29 (Nina): it leaves the moment you
    // click into the box, and no placeholder takes its place (floorLineSlot
    // keeps the native one blank while focused).
    const floorLine = floor ? (floor.note || floor.presence || '') : '';
    const floorLineSlot = !!floorLine && !message && interjectionsRemaining > 0 && !thinkingOverlayOn && !roomHintOn && !resting;
    const floorLineOn = floorLineSlot && !composerFocused;
    return (
      <form ref={formRootRef} onSubmit={handleSubmit} className={'human-input-form' + (!(competitiveMode || imposterMode) ? ' hif-freechat' : '')} style={frameOn ? {
        // 🧱 the room frame: no glass around the box (the box row below is it)
        backgroundColor: 'transparent', borderRadius: 0, padding: 0, border: 0,
      } : {
        // No backdrop blur (Nina's ruling 08-14): the feed + ambient canvas
        // move behind this composer every frame, so blur(12px) re-sampled
        // continuously — the last always-on blur-over-motion (fx-bench 07-09:
        // blur family = -37% decoration cost). Darker tint carries the glass.
        backgroundColor: 'rgba(0,0,0,0.6)',
        borderRadius: '14px',
        padding: '8px',
        border: `1px solid rgba(255,255,255,0.06)`,
      }}>
        {frameOn && <style>{FRAME_CSS}</style>}
        {/* Simplified Header — competitive/imposter only */}
        {(competitiveMode || imposterMode) && (
        <div style={{
          marginBottom: '8px',
          paddingBottom: '6px',
          paddingLeft: '4px',
          borderBottom: `1px solid rgba(255,255,255,0.06)`
        }}>
          <h4 style={{
            margin: '0',
            color: THEME.accent,
            fontSize: '0.8em',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>Game Chat</span>
            <span style={{
              color: interjectionsRemaining <= 1 ? (THEME.error || '#F44336') : 'rgba(255,255,255,0.35)',
              fontSize: '0.75em',
              fontWeight: 500
            }}>
              {interjectionsRemaining} left
            </span>
          </h4>
        </div>
        )}

        {renderBanNotice()}

        {/* Error Display */}
        {error && (
          <div style={{
            backgroundColor: `${errorTone}15`,
            color: errorTone,
            padding: '6px 10px',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '0.8em',
            border: `1px solid ${errorTone}30`
          }}>
            {error}
          </div>
        )}

        {/* ⏱ Frequency-limiter countdown — the ONE message lock with a real
            clock (429 retry_after). Ticks down live; Send re-enables at 0.
            Typing stays open on purpose — only sending waits. */}
        {rateWaitSec > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(244, 160, 60, 0.12)',
            color: THEME.warning || '#F4A03C',
            padding: '6px 10px',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '0.8em',
            border: '1px solid rgba(244, 160, 60, 0.35)',
          }}>
            <span style={{ flex: 1 }}>⏱ Sending fast! You can send again in {rateWaitSec}s</span>
          </div>
        )}

        {/* 🔄 Refill chip — the visible round boundary. Announces the budget
            returning after a full lockout, then hides itself after 5s. */}
        {showRefilled && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: `${THEME.accent || '#7C4DFF'}18`,
            color: THEME.accent || '#7C4DFF',
            padding: '6px 10px',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '0.8em',
            border: `1px solid ${THEME.accent || '#7C4DFF'}35`,
          }}>
            <span style={{ flex: 1 }}>New round! Messages refilled</span>
          </div>
        )}

        {/* Queued-send chip — send was pressed while the AI turn lock was on */}
        {queuedSend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: `${THEME.accent || '#7C4DFF'}18`,
            color: THEME.accent || '#7C4DFF',
            padding: '6px 10px',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '0.8em',
            border: `1px solid ${THEME.accent || '#7C4DFF'}35`,
          }}>
            <span style={{ flex: 1 }}>⏳ Queued — sends when the current turn finishes</span>
            <button
              type="button"
              onClick={() => setQueuedSend(false)}
              title="Cancel queued send"
              style={{
                border: 'none', background: 'transparent', color: 'inherit',
                cursor: 'pointer', fontWeight: 700, fontSize: '1em', padding: '0 2px',
              }}
            >✕</button>
          </div>
        )}

        {/* ＋ TRAY (places nav, Nina 08-01 — icon dock, mock v3 = contract):
            the tool verbs regrouped as 42px stroke-icon circles above the
            input. Tiles exist only when their handler does (tool_flags
            gating upstream = no tile). Any pick closes the tray. */}
        {trayOn && trayOpen && (
          <div data-composer-tray={frameOn ? '' : undefined} style={frameOn ? {
            // 🧱 the room frame: the same tray on the frame's tokens
            display: 'flex', gap: 14, justifyContent: 'center',
            padding: '10px 8px 12px', marginBottom: 8,
            background: 'var(--rs-list, #12151A)',
            border: 0, boxShadow: 'inset 0 0 0 1px var(--rs-line, #262B34)',
            borderRadius: 14,
          } : {
            display: 'flex', gap: 14, justifyContent: 'center',
            padding: '10px 8px 12px', marginBottom: 6,
            background: 'rgba(20,16,32,0.98)',
            border: `1px solid ${THEME.accent || '#7C4DFF'}55`,
            borderRadius: 14,
          }}>
            {[
              onOpenDrawing && { k: 'doodle', label: 'Doodle', aria: 'Open drawing canvas', run: onOpenDrawing },
              onUploadFile && !frameShareIsUpload && { k: 'upload', label: 'Upload', aria: 'Upload a file', run: () => fileInputRef.current?.click() },
              canUseCommands && { k: 'actions', label: 'Actions', aria: 'Open actions palette', run: () => insertCommand('') },
              onOpenShelf && { k: 'shelf', label: 'Shelf', aria: 'Open the space shelf', run: onOpenShelf },
              // Poke moved off the send row into the tray (Nina 08-02)
              onPoke && { k: 'poke', label: resting ? ASLEEP_COPY.play : 'Poke', aria: resting ? ASLEEP_COPY.playTitle : 'Poke the AIs to keep talking', run: onPoke, off: !!resting },
            ].filter(Boolean).map(t => (
              <button
                key={t.k}
                type="button"
                aria-label={t.aria}
                disabled={isSubmitting || !!t.off || (t.k === 'upload' && uploadingFile) || (t.k === 'poke' && isPoking)}
                onClick={() => { setTrayOpen(false); t.run(); }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <span style={frameOn ? {
                  width: 42, height: 42, borderRadius: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--rs-hover, #1A1E25)', color: 'var(--rs-acc-t, #4F8CFF)',
                } : {
                  width: 42, height: 42, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(255,255,255,0.06)',
                  border: `1px solid ${THEME.accent || '#7C4DFF'}60`,
                  color: '#c9b8ff',
                }}>
                  {t.off ? '💤' : (t.k === 'upload' && uploadingFile) || (t.k === 'poke' && isPoking) ? '⏳' : <LineIcon name={t.k} size={17} />}
                </span>
                <span style={frameOn ? { fontSize: 11, fontWeight: 600, color: 'var(--rs-muted, #8B92A0)' } : { fontSize: 8, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>{t.label}</span>
              </button>
            ))}
          </div>
        )}
        {/* Textarea + Send/Poke + tool icons. Desktop: one row, buttons in
            stacked columns beside the textarea. Phones: the 3-icon column
            forced the whole block to ~128px and starved the textarea — the
            row wraps instead (Nina 07-20): textarea full-width on top, ONE
            40px strip below (⚡📎🖍 left · 👉 ➤ right, Send in the thumb
            corner). trayMode replaces that left strip with the single ＋. */}
        {/* 💬 Message budget in FREE-CHAT spaces (Nina 08-20: "I don't see
            the counter") — the "N left" chip lives in the header above, which
            only renders for competitive/imposter, so non-comp rooms hit
            "No messages remaining" with zero warning. Hidden at full budget
            (no noise), appears once a message is spent, red on the last one,
            and at zero says the budget comes back. Max mirrors
            MAX_INTERJECTIONS_PER_ROUND in GameStepPage (non-comp = 3). */}
        {!(competitiveMode || imposterMode) && interjectionsRemaining < 3 && (
          <div style={{
            textAlign: 'right', padding: '0 2px 4px',
            fontSize: '0.7em', fontWeight: 600,
            color: interjectionsRemaining <= 1 ? (THEME.error || '#F44336') : 'rgba(255,255,255,0.45)',
          }}>
            {resting
              ? (interjectionsRemaining > 0 ? ASLEEP_COPY.notesLeft(interjectionsRemaining) : ASLEEP_COPY.notesDone)
              : interjectionsRemaining > 0
              ? `${interjectionsRemaining} ${interjectionsRemaining === 1 ? 'message' : 'messages'} left this round`
              : 'Refreshing message stash...'}
          </div>
        )}
        {/* ⏳🎬 THE COUNTDOWN LINE (09-22, Nina: A+B, all day, every calendar
            row, the title once the ballot closes, a sweep before the doors).
            One line, never floating, ✕ hides it per device until the STATE
            changes (a new phase = a new key). The model is pure
            (calendarView.eventStrip); the page ticks it and owns the tap. */}
        {eventStrip && eventStrip.text && stripHiddenKey !== `${eventStrip.key}|${eventStrip.phase}` && (
          <div data-event-strip={eventStrip.phase} role="status" style={{
            display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
            margin: '0 0 6px', padding: '5px 6px 5px 9px', borderRadius: 8,
            border: `1px solid ${eventStrip.hot ? 'rgba(233,181,101,0.75)' : 'rgba(233,181,101,0.35)'}`,
            background: eventStrip.hot ? 'rgba(233,181,101,0.14)' : 'rgba(233,181,101,0.07)',
            color: '#f3d9a6', fontSize: '0.8em', lineHeight: 1.25,
          }}>
            <span aria-hidden="true" style={{ flexShrink: 0 }}>{eventStrip.emoji}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{eventStrip.text}</span>
            {eventStrip.clock && (
              <span data-event-clock="1" style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: eventStrip.hot ? '#ffd98a' : '#e9b565', whiteSpace: 'nowrap' }}>{eventStrip.clock}</span>
            )}
            <button type="button" data-event-go={eventStrip.go}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { if (eventStrip.onGo) eventStrip.onGo(eventStrip); }}
              style={{ flexShrink: 0, border: '1px solid rgba(233,181,101,0.6)', background: 'rgba(233,181,101,0.16)', color: '#ffd98a', borderRadius: 6, padding: '2px 8px', fontSize: '0.95em', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {eventStrip.cta}
            </button>
            <button type="button" aria-label="Hide until this changes" title="Hide until this changes"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => hideEventStrip(`${eventStrip.key}|${eventStrip.phase}`)}
              style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'rgba(243,217,166,0.6)', cursor: 'pointer', padding: '0 2px', fontSize: '1em', lineHeight: 1 }}>✕</button>
          </div>
        )}
        {/* 🌙 W2b: "💤 Resting since 4:20pm · back after the next top-up" */}
        <RestingBanner resting={resting} />
        <div className={frameOn ? 'hif-frow' : undefined} style={frameOn ? {
          // 🧱 the room frame: THE box (mockup .cbox), one row at every width
          display: 'flex', gap: '6px', position: 'relative', flexWrap: 'nowrap', alignItems: 'flex-end',
          padding: '7px', borderRadius: '14px', background: 'var(--rs-list, #12151A)', boxSizing: 'border-box',
        } : { display: 'flex', gap: '8px', position: 'relative', flexWrap: (isMobile || narrowDesktop) ? 'wrap' : 'nowrap' }}>
          {renderMentionDropdown()}
          {renderHashDropdown()}
          {renderCommandPalette()}
          {renderCommandArgHint()}
          {/* 📥 THE BOX (09-20): the frame the textarea used to draw itself now
              belongs to this wrapper, so the doors strip can live inside it.
              ⚠️ ALWAYS rendered, in every space and at every width: a wrapper
              that came and went with houseRooms would REMOUNT the textarea
              (dropped focus, collapsed phone keyboard) when the first status
              payload lands. Only the strip inside it is conditional. A press
              on the strip's empty space focuses the box, like any chat app. */}
          <div className="hif-box"
            onMouseDown={(e) => {
              // 🧭 10-01 composer: the chip lane's empty floor (and the gaps in its run) focuses the box too
              if (e.target === e.currentTarget || (e.target.dataset && (e.target.dataset.doorStrip || e.target.dataset.roomFloor || e.target.dataset.floorRun))) {
                e.preventDefault();
                if (textareaRef.current) textareaRef.current.focus();
              }
            }}
            style={frameOn ? {
              // 🧱 the room frame: the line lives INSIDE the box row (the row
              // draws the frame and the focus ring), the widest thing in it
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
              position: 'relative', borderRadius: 0, border: 'none', backgroundColor: 'transparent', cursor: 'text',
            } : {
              flex: 1,
              // Desktop: the input must stay the row's widest element — the
              // fixed siblings shrink instead (user, 07-11). Phones AND
              // narrow-desktop containers: it owns the full top row
              // (flexWrap above), and narrow desktop gets a TALLER box —
              // the whole point of the reflow (Nina 08-14).
              minWidth: (isMobile || narrowDesktop) ? '100%' : '45%',
              display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
              // the who-is-here line (floorLineOn) sits inside the box
              position: 'relative',
              borderRadius: '4px',
              // Non-competitive spaces: full-strength accent border + darker fill so
              // the box reads as "type here" against busy space art (user call 07-11)
              border: !(competitiveMode || imposterMode)
                ? `1.5px solid ${THEME.accent || '#7C4DFF'}90`
                : `1px solid ${THEME.accent || '#7C4DFF'}40`,
              backgroundColor: !(competitiveMode || imposterMode) ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.3)',
              cursor: 'text',
            }}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onFocus={handleComposerFocus}
            onBlur={handleComposerBlur}
            onKeyDown={(e) => handleKeyDownWithMention(e, handleSubmit)}
            // 📱 08-01: the 07-16 scrollIntoView re-pin is GONE. It predated the
            // 07-17 fixed thumb dock by a day — on every focus it fought the
            // dock's visualViewport translate for 1.5s (two keyboard
            // strategies), and scrollIntoView on a fixed-positioned ancestor
            // scrolls the DOCUMENT. The dock translate alone owns the keyboard
            // now (GameStepPage, grep "translate the dock").
            placeholder={(thinkingOverlayOn || roomHintOn || floorLineSlot) ? '' : usualPlaceholder}
            // readOnly, NOT disabled, during submit (08-02 scroll-up RCA):
            // disabling a focused textarea BLURS it → typingFocus drops → the
            // mobile dock/keyboard collapse and the page's bottom reserve
            // shrinks → the browser clamps scroll = "feed scrolls up after
            // every send". readOnly keeps focus (and the keyboard) through
            // the submit; the Send button still disables on isSubmitting.
            // 09-20: a free-chat send empties the box at send START, so typing
            // the next line mid-flight is safe (nothing clears it later). Only
            // the TURN path still clears on success and keeps the lock. The ref
            // flips in the same tick as isSubmitting, so this render sees it.
            readOnly={isSubmitting && !interjectionInFlightRef.current}
            disabled={interjectionsRemaining <= 0}
            maxLength={LIMITS.INTERJECTION_MAX}
            // 🏡 W2d: the place page's box is one line (mockup v9), like a note
            rows={(placeDesk || frameOn) ? 1 : undefined}
            style={{
              // frame, fill and row-width now live on .hif-box above; the
              // textarea fills it and keeps its own height limits + padding
              // (the overlays below are measured against exactly these).
              flex: '1 1 auto', width: '100%', display: 'block',
              minHeight: narrowDesktop ? '64px' : '40px',
              maxHeight: narrowDesktop ? '140px' : '80px',
              padding: '8px 10px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: '#fff',
              fontSize: isMobile ? '1em' : '0.9em',
              fontFamily: 'inherit',
              resize: 'none',
              boxSizing: 'border-box',
              outline: 'none',
              lineHeight: '1.4',
              // 🧱 the room frame: one line tall like its buttons (mockup .cin),
              // 16px on phones (no iOS zoom), the frame's ink
              ...(frameOn ? {
                minHeight: isMobile ? '40px' : '36px', maxHeight: '140px', padding: isMobile ? '9px 6px' : '8px 6px',
                fontSize: isMobile ? '16px' : '14.5px', color: 'var(--rs-ink, #ECEEF2)',
              } : null),
            }}
          />
          {floorLineOn && (
            <div data-floor-presence="1" style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              // exactly where the placeholder sits: the textarea's 8px/10px
              // padding, same font size and line height
              padding: '8px 10px', boxSizing: 'border-box', pointerEvents: 'none', zIndex: 1,
              display: 'flex', alignItems: 'baseline', gap: 6, overflow: 'hidden', whiteSpace: 'nowrap',
              fontSize: isMobile ? '1em' : '0.9em', lineHeight: '1.4', fontFamily: 'inherit',
            }}>
              {/* who is here wins the width: "Say something..." gives way first (phones) */}
              <span style={{ color: '#e6dac6', fontWeight: 700, fontStyle: floor.note ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, maxWidth: '100%', flex: '0 0 auto' }}>{floorLine}</span>
              <span style={{ color: 'rgba(255,255,255,0.55)', flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>Say something...</span>
            </div>
          )}
          {/* 🚪📅 desktop: the doors, on the floor of the box */}
          {doorsInBox && (
            <div data-door-strip="1" style={{
              display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, minWidth: 0,
              padding: '0 4px 4px', boxSizing: 'border-box',
            }}>
              {renderDoors(false)}
              {/* 🧭 10-01 composer: no divider before the chips (they sit right now, the empty floor between is the gap) */}
              {renderFloorLane(false)}
            </div>
          )}
          </div>
          {/* 🚪📅 phones: the doors ride the tool strip, far left */}
          {hasDoors && isMobile && (
            <div data-door-strip="1" style={{ order: 1, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, minWidth: 0 }}>
              {renderDoors(true)}
            </div>
          )}
          {floor && isMobile && (
            <div style={{ order: 1, flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center' }}>
              {renderFloorLane(true)}
            </div>
          )}
          {/* 🤔📥 In-input thinking overlay (Nina 08-27): when the composer is
              idle (empty + unfocused) the indicator sits INSIDE the input box,
              pinned over the (blanked) placeholder. pointerEvents:none — a tap
              lands on the textarea underneath, focuses it, and the overlay
              yields to the strip-row slot below. height matches the empty
              textarea's 40px mobile min-height. */}
          {roomHintOn && (
            <div data-room-hint="1" style={{
              position: 'absolute', top: 0, left: leftDoorsW, right: 0,
              // sits exactly where the placeholder would: the textarea's 1.5px
              // border + 8px/10px padding, same font size and line height
              padding: '9.5px 11.5px', boxSizing: 'border-box', pointerEvents: 'none', zIndex: 1,
              display: 'flex', alignItems: 'baseline', gap: 7, overflow: 'hidden', whiteSpace: 'nowrap',
              fontSize: isMobile ? '1em' : '0.9em', lineHeight: '1.4', fontFamily: 'inherit',
            }}>
              <span style={{ color: '#ff5f52', fontWeight: 800, flexShrink: 0 }}>New! Try /room</span>
              <span style={{ color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{usualPlaceholder}</span>
            </div>
          )}
          {thinkingOverlayOn && (
            <div aria-live="polite" style={{
              position: 'absolute', top: 0, left: leftDoorsW, right: 0, height: 40,
              padding: '0 10px', display: 'flex', alignItems: 'center', gap: 5,
              pointerEvents: 'none', zIndex: 1,
              color: 'rgba(255,255,255,0.75)', fontSize: '0.8em', fontWeight: 600,
            }}>
              {!thinkingCandle && <span aria-hidden style={{ fontSize: '1.2em', animation: 'thinking 1.5s ease-in-out infinite' }}>🤔</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {thinkingStatus} {thinkingCandle ? 'is thinking' : 'is thinking...'}
              </span>
              {thinkingCandle && <CandleHop style={{ fontSize: '1.1em' }} />}
            </div>
          )}
          {/* 🤔 Inline thinking status (Nina 08-06): while the dock is open the
              top-floating "X is thinking" pill relocates HERE — the strip row's
              dead middle, left of ＋/Send. order 2 ties with the send group and
              DOM order keeps it left; flex:1 + minWidth:0 lets long local names
              ellipsize instead of shoving the buttons. The `thinking` keyframe
              comes from TypingIndicator's global stylesheet (imported by
              GameStepPage, so it's always present on this page). Yields to the
              in-input overlay above while the composer is idle (08-27). */}
          {isMobile && thinkingStatus && !thinkingOverlayOn && (
            <div aria-live="polite" style={{
              order: 2, flex: '1 1 0', minWidth: 0, alignSelf: 'center',
              display: 'flex', alignItems: 'center', gap: 5,
              color: 'rgba(255,255,255,0.55)', fontSize: '0.72em', fontWeight: 600,
            }}>
              {!thinkingCandle && <span aria-hidden style={{ fontSize: '1.2em', animation: 'thinking 1.5s ease-in-out infinite' }}>🤔</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {thinkingStatus} {thinkingCandle ? 'is thinking' : 'is thinking...'}
              </span>
              {thinkingCandle && <CandleHop style={{ fontSize: '1.1em' }} />}
            </div>
          )}
          {/* 🧱 The room frame's labeled Share (09-29, SPACES_REDESIGN §5.5a; mockup
              rooms-by-purpose.html frame A, "Share with the room"). Only the ONE
              composer the frame mounts passes `share`: nothing here renders
              anywhere else. Doc · Image · Video set the filter on their own
              hidden input and ride onUploadFile (the 📎's path, its 7 MB cap);
              Screen is the page's LivestreamPanel start (desktop, and only where
              getDisplayMedia exists). Armored like the frame: classes free of the
              words room themes target, data-prop-ui on every button. */}
          {share && onUploadFile && (() => {
            const shareItems = shareMenuItems({ canScreen: !isMobile && typeof share.onScreen === 'function' && canShareScreen() });
            const pickShare = (it) => {
              setShareOpen(false);
              if (it.key === 'screen') { share.onScreen(); return; }
              const el = shareInputRef.current;
              if (!el) return;
              el.setAttribute('accept', it.accept);
              el.click();
            };
            return (
              <div ref={shareWrapRef} className="hif-shr" data-composer-share="" data-phone={isMobile ? '1' : '0'} style={(isMobile || frameOn) ? { order: 1, ...(frameOn ? { alignSelf: 'flex-end' } : null) } : undefined}>
                <style>{SHARE_CSS}</style>
                <button type="button" data-prop-ui="" className="hif-shrb" aria-haspopup="menu" aria-expanded={shareOpen}
                  disabled={uploadingFile} title="Share with the room" onClick={() => setShareOpen((o) => !o)}>
                  {uploadingFile ? <span aria-hidden="true">⏳</span> : <LineIcon name="upload" size={15} />}
                  <span>Share</span>
                </button>
                {shareOpen && (
                  <div className="hif-shrm" role="menu" aria-label="Share with the room">
                    <div className="hif-shrh" aria-hidden="true">Share with the room</div>
                    {shareItems.map((it) => (
                      <button key={it.key} type="button" role="menuitem" data-prop-ui="" className="hif-shri" data-share-item={it.key} onClick={() => pickShare(it)}>
                        <span className="hif-shric" aria-hidden="true">{SHARE_ICONS[it.key]}</span>
                        <span className="hif-shrl">{it.label}</span>
                        <span className="hif-shrk">{it.hint}</span>
                      </button>
                    ))}
                    {/* 📺 Stream Eyes: every go-live CTA carries the consent line (Nina's call) */}
                    {shareItems.some((it) => it.key === 'screen') && (
                      <div className="hif-shrn" data-share-note="">{SHARE_ICONS.eye}<span>{SHARE_SCREEN_NOTE}</span></div>
                    )}
                  </div>
                )}
                <input
                  ref={shareInputRef}
                  type="file"
                  data-share-input=""
                  style={{ display: 'none' }}
                  onChange={uploadPickedFile}
                />
              </div>
            );
          })()}
          {/* Send/Poke: stacked column on desktop; on phones they sit at the
              RIGHT end of the strip row, Send outermost (row-reverse + order 2
              + marginLeft auto pushes them past the tool icons). */}
          <div style={{
            display: 'flex',
            // Narrow desktop: the stacked column joins the wrapped strip row
            // and stretches — Send/Poke split it as big labeled targets.
            flexDirection: isMobile ? 'row-reverse' : ((narrowDesktop || placeDesk) ? 'row' : 'column'),
            gap: placeDesk ? '8px' : '4px', flexShrink: 0,
            ...(placeDesk ? { alignItems: 'center' } : null),
            ...(isMobile ? { order: 2, marginLeft: 'auto' } : null),
            ...(!isMobile && narrowDesktop ? { flex: '1 1 auto' } : null),
            // 🧱 the room frame: the end of the box row (👉 then ➤, Send last), one line tall
            ...(frameOn ? { flexDirection: 'row-reverse', gap: '6px', alignItems: 'center', order: 2, marginLeft: 0, flex: '0 0 auto' } : null),
          }}>
            {/* Send button (🏡 not on the desktop place page: Enter sends) */}
            {!placeDesk && (
            <button
              type="submit"
              disabled={sendDisabled}
              className={frameOn ? 'hif-frs' : undefined}
              // ⏎➤ Keep focus in the textarea (Nina 08-06 "tap Send twice"):
              // without this, the tap blurs the composer first, typingFocus
              // flips, the dock re-layouts, and the button moves out from
              // under the finger before the click lands. Same
              // mousedown-preventDefault the palette rows use.
              onMouseDown={(e) => e.preventDefault()}
              style={{
                // Slimmer on small screens — the 28px side padding was
                // starving the textarea (user, 07-11)
                padding: isMobile ? '6px 12px' : '6px 20px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: THEME.accent || '#7C4DFF',
                // Theme-derived ink: dark on light accents (08-02 contrast pass)
                color: THEME.textOnAccent || '#fff',
                fontSize: '0.9em',
                fontWeight: 600,
                cursor: sendDisabled ? 'default' : 'pointer',
                opacity: sendDisabled ? 0.5 : 1,
                transition: 'all 0.2s',
                // Column: stretch to share height. Strip row: fixed 40px tall,
                // wide enough to read as the primary action.
                flex: isMobile ? '0 0 auto' : 1,
                ...(isMobile ? { height: '40px', minWidth: '62px' } : null),
                // 🧱 the room frame (mockup .csend): a square accent key, the
                // frame's own fill + ink
                ...(frameOn ? {
                  width: isMobile ? '40px' : '38px', height: isMobile ? '40px' : '38px', minWidth: 0, padding: 0, flex: '0 0 auto',
                  borderRadius: '10px', backgroundColor: 'var(--rs-acc-fill, #4F8CFF)', color: 'var(--rs-acc-on, #0B0D10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
                } : null),
              }}
              title={isSubmitting ? 'Sending...' : interjectionsRemaining <= 0 ? 'No messages left this round' : queuedSend ? 'Queued — sends when the current turn finishes' : 'Send'}
              aria-label="Send"
            >
              {/* Icon-only on phones (Nina 07-17): the labeled column starved
                  the textarea; title/aria keep the meaning */}
              {isSubmitting ? '...' : queuedSend ? '⏳' : frameOn ? SHARE_ICONS.send : (isMobile ? '➤' : 'Send')}
            </button>
            )}
            {/* Poke button — on tray-mode phones it lives INSIDE the ＋ tray
                (Nina 08-02); desktop and non-tray layouts keep it here */}
            {onPoke && !trayOn && (
              <button
                type="button"
                onClick={onPoke}
                disabled={pokeDisabled}
                className={frameOn ? 'hif-frt' : undefined}
                style={frameOn ? { ...frameTool, cursor: pokeDisabled ? 'default' : 'pointer', opacity: pokeDisabled ? 0.5 : 1, background: 'transparent', color: 'var(--rs-muted, #8B92A0)', boxShadow: 'inset 0 0 0 1px var(--rs-line, #262B34)' }
                  : placeDesk ? { ...placeBtn, cursor: pokeDisabled ? 'default' : 'pointer', opacity: pokeDisabled ? 0.5 : 1 } : {
                  padding: isMobile ? '6px 12px' : '6px 20px',
                  borderRadius: '4px',
                  border: resting ? '1px solid rgba(174,182,224,0.3)' : pokeDisabled ? '1px solid rgba(255,255,255,0.06)' : `1px solid ${THEME.accent || '#7C4DFF'}35`,
                  background: 'transparent',
                  color: resting ? '#c2caf4' : pokeDisabled ? 'rgba(255,255,255,0.2)' : (THEME.accentOnPanel || THEME.accent || '#7C4DFF'),
                  cursor: pokeDisabled ? 'default' : 'pointer',
                  fontSize: '0.82em',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  transition: 'all 0.2s',
                  flex: isMobile ? '0 0 auto' : 1,
                  ...(isMobile ? { height: '40px', minWidth: '44px' } : null),
                }}
                onMouseEnter={(e) => {
                  if (!pokeDisabled && !placeDesk && !frameOn) {
                    e.currentTarget.style.background = `${THEME.accent || '#7C4DFF'}15`;
                    e.currentTarget.style.borderColor = `${THEME.accent || '#7C4DFF'}60`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (placeDesk || resting || frameOn) return;
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = pokeDisabled ? 'rgba(255,255,255,0.06)' : `${THEME.accent || '#7C4DFF'}35`;
                }}
                title={resting ? ASLEEP_COPY.playTitle : placeDesk ? `Nudge ${placeName || 'them'} to keep talking` : 'Poke the AIs to keep talking'}
                aria-label={resting ? ASLEEP_COPY.playTitle : placeDesk ? `Nudge ${placeName || 'them'}` : frameOn ? 'Poke the AIs to keep talking' : undefined}
              >
                {frameOn ? (isPoking ? <span aria-hidden="true">⏳</span> : resting ? <span aria-hidden="true">💤</span> : <LineIcon name="poke" size={16} />) : (
                <span style={{ fontSize: isMobile ? '1.15em' : '0.95em' }}>
                  {isPoking ? '⏳' : resting ? '💤' : (placeDesk ? '👋' : '👉')}
                </span>
                )}
                {(isMobile || placeDesk || frameOn) ? null : (isPoking ? 'Poking...' : resting ? ASLEEP_COPY.play : 'Poke')}
              </button>
            )}
            {/* 📱 ＋ tray toggle — moved beside Send (Nina 08-02, was the
                lone left-strip resident); row-reverse puts it LEFT of Send.
                The hidden file input stays mounted for the tray's Upload. */}
            {trayMode && isMobile && !frameOn && (
              <button
                type="button"
                onClick={() => setTrayOpen(o => !o)}
                aria-label={trayOpen ? 'Close tools' : 'Open tools'}
                aria-expanded={trayOpen}
                style={{
                  width: '40px', height: '40px', padding: 0, flexShrink: 0,
                  borderRadius: '4px',
                  border: `1px solid ${THEME.accent || '#7C4DFF'}45`,
                  background: trayOpen ? `${THEME.accent || '#7C4DFF'}40` : `${THEME.accent || '#7C4DFF'}16`,
                  color: trayOpen ? '#fff' : (THEME.accent || '#7C4DFF'),
                  fontSize: '1.1em', fontWeight: 700, lineHeight: 1, cursor: 'pointer',
                }}
              >{trayOpen ? '✕' : '＋'}</button>
            )}
          </div>
          {/* Hidden file input — mounted OUTSIDE the tool column (the column
              skips on tray-mode phones, but the tray's Upload tile still
              clicks this). display:none, so placement is cosmetic. */}
          {onUploadFile && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,application/pdf,text/*,.txt,.md,.markdown,.csv,.docx"
              style={{ display: 'none' }}
              onChange={uploadPickedFile}
            />
          )}
          {/* ⚡ + 📎 + 🖍️ — ONE slim stacked column (two full-height 40px columns
              ate the textarea's width on small screens, user call 07-11).
              The input box must always be the widest thing in this row.
              Tray-mode phones: the column's only resident (＋) moved into the
              send group on the right (Nina 08-02), so the column skips. */}
          {!trayOn && ((onUploadFile && !frameShareIsUpload) || onOpenDrawing || canUseCommands) && (
            <div style={{
              display: 'flex',
              // Narrow desktop: icon column lies down into the strip row
              // beside Send/Poke (same shape phones use).
              flexDirection: (isMobile || narrowDesktop || placeDesk) ? 'row' : 'column',
              gap: (isMobile || narrowDesktop || placeDesk) ? '8px' : '4px',
              flexShrink: 0, justifyContent: 'center',
              ...(placeDesk ? { alignItems: 'center' } : null),
              ...(isMobile ? { order: 1 } : null),
              // 🧱 the room frame: the tools open the box, left of the line (mockup "+")
              ...(frameOn ? { flexDirection: 'row', gap: '6px', alignItems: 'center', order: -1 } : null),
            }}>
              {!trayOn && canUseCommands && (
                <button
                  type="button"
                  onClick={() => insertCommand('')}
                  disabled={isSubmitting}
                  title="Actions (/ship, /drama, /scene, /talk)"
                  aria-label="Open actions palette"
                  className={frameOn ? 'hif-frt' : undefined}
                  style={frameOn ? { ...frameTool, cursor: 'pointer' } : placeDesk ? { ...placeBtn, order: 2, fontWeight: 700, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', cursor: 'pointer' } : {
                    // 40px on phones (44px audit floor, capped so the 3-stack
                    // still fits beside the composer — mobile audit wave 3)
                    width: isMobile ? '40px' : '28px',
                    height: isMobile ? '40px' : '28px',
                    padding: 0,
                    flexShrink: 0,
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 200, 100, 0.35)',
                    background: 'transparent',
                    fontSize: '0.8em',
                    lineHeight: 1,
                    cursor: 'pointer',
                    // 🖊️ Stroke set, same as the mobile tray (Nina 08-03:
                    // desktop emoji trio → LineIcons for platform parity)
                    color: '#c9b8ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >{placeDesk ? <span aria-hidden="true">/</span> : <LineIcon name="actions" size={15} />}</button>
              )}
              {onUploadFile && !frameShareIsUpload && (
                <>
                  {!trayOn && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting || uploadingFile}
                    title="Share a file with the room (images, videos, PDFs, docs)"
                    aria-label="Upload a file"
                    style={placeDesk ? { ...placeBtn, order: 3, color: 'rgba(245,197,128,0.75)', border: '1px solid rgba(240,172,85,0.22)', background: 'transparent', cursor: uploadingFile ? 'default' : 'pointer', opacity: uploadingFile ? 0.5 : 1 } : {
                      // 40px on phones like its ⚡/🖍 siblings (the wave-3
                      // 28→40 bump missed this one)
                      width: isMobile ? '40px' : '28px',
                      height: isMobile ? '40px' : '28px',
                      padding: 0,
                      flexShrink: 0,
                      borderRadius: '4px',
                      border: `1px solid ${THEME.accent || '#7C4DFF'}30`,
                      background: 'transparent',
                      fontSize: '0.8em',
                      lineHeight: 1,
                      cursor: uploadingFile ? 'default' : 'pointer',
                      opacity: uploadingFile ? 0.5 : 1,
                      color: '#c9b8ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >{uploadingFile ? '⏳' : <LineIcon name="upload" size={15} />}</button>
                  )}
                </>
              )}
              {!trayOn && onOpenDrawing && (
                <button
                  type="button"
                  onClick={onOpenDrawing}
                  disabled={isSubmitting}
                  title={placeDesk ? `Draw something for ${placeName || 'the room'}` : 'Draw something for the room'}
                  aria-label="Open drawing canvas"
                  className={frameOn ? 'hif-frt' : undefined}
                  style={frameOn ? { ...frameTool, cursor: 'pointer' } : placeDesk ? { ...placeBtn, order: 1, cursor: 'pointer' } : {
                    // 40px on phones (44px audit floor, capped so the 3-stack
                    // still fits beside the composer — mobile audit wave 3)
                    width: isMobile ? '40px' : '28px',
                    height: isMobile ? '40px' : '28px',
                    padding: 0,
                    flexShrink: 0,
                    borderRadius: '4px',
                    border: `1px solid ${THEME.accent || '#7C4DFF'}30`,
                    background: 'transparent',
                    fontSize: '0.8em',
                    lineHeight: 1,
                    cursor: 'pointer',
                    color: '#c9b8ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >{placeDesk ? <span aria-hidden="true">✏️</span> : <LineIcon name="doodle" size={15} />}</button>
              )}
            </div>
          )}
          {/* 🧱 the room frame on a phone: the ＋ that opens the tray, at the
              box's far left (mockup frame A phone: [+] [the line] [Send]) */}
          {frameOn && isMobile && (
            <button
              type="button"
              className="hif-frt"
              onClick={() => setTrayOpen(o => !o)}
              aria-label={trayOpen ? 'Close tools' : 'Open tools'}
              aria-expanded={trayOpen}
              style={{ ...frameTool, order: -1, alignSelf: 'flex-end', cursor: 'pointer', fontSize: '1.1em', fontWeight: 600,
                ...(trayOpen ? { background: 'var(--rs-acc-soft, rgba(79,140,255,0.15))', color: 'var(--rs-acc-t, #4F8CFF)' } : null) }}
            >{trayOpen ? '✕' : '＋'}</button>
          )}
        </div>

        {/* Char count. ⌨️ Slim dock while typing (Nina 08-06): on phones the
            row hides while the composer owns focus — it only earns its space
            near the cap (>90%), where it pops back as the red warning. */}
        {/* (🧱 the room frame: only near the cap, like a phone mid-typing) */}
        {!(isMobile && composerFocused && message.length <= LIMITS.INTERJECTION_MAX * 0.9) && !(frameOn && message.length <= LIMITS.INTERJECTION_MAX * 0.9) && (
        <div style={{
          marginTop: '4px',
          paddingLeft: '2px',
        }}>
          <div style={{
            fontSize: '0.65em',
            color: message.length > LIMITS.INTERJECTION_MAX * 0.9 ? THEME.error : 'rgba(255,255,255,0.2)',
            fontWeight: message.length > LIMITS.INTERJECTION_MAX * 0.9 ? 700 : 400,
            opacity: message.length > 0 ? 1 : 0,
            transition: 'opacity 0.15s',
          }}>
            {message.length}/{LIMITS.INTERJECTION_MAX}
          </div>
        </div>
        )}

        {/* Play Round button (injected from parent) */}
        {playRoundButton && <div style={{ marginTop: '6px' }}>{playRoundButton}</div>}

      </form>
    );
  }

  // Pulsing glow keyframe for active turn forms
  const pulseGlowStyle = (
    <style>{`
      @keyframes turnPulseGlow {
        0%, 100% { box-shadow: 0 0 15px rgba(100,180,255,0.3), 0 0 30px rgba(80,160,255,0.15); }
        50% { box-shadow: 0 0 30px rgba(100,180,255,0.65), 0 0 60px rgba(80,160,255,0.3), 0 0 4px rgba(100,180,255,0.8); }
      }
    `}</style>
  );

  // Imposter (Glitch) mode, VOTE turn (poll-recovery path — the VotingModal
  // owns the WS-delivered case): player-pick, never the one-word form.
  if (imposterMode && isYourTurn && pendingTurnType === 'vote' && onSubmitVote && players.length > 0) {
    const voteSubmitted = turnSubmitted || isSubmitting;
    const handlePickVote = async (p) => {
      if (voteSubmitted) return;
      setIsSubmitting(true);
      setError(null);
      try {
        await onSubmitVote({ voted_for: p.game_player_id, target_name: p.name });
        setTurnSubmitted(true);
      } catch (err) {
        setError(err.message || 'Vote failed. Try again!');
      } finally {
        setIsSubmitting(false);
      }
    };
    return (
      <div className="human-input-form" style={{
        backgroundColor: voteSubmitted ? 'rgba(40,40,50,0.7)' : 'rgba(20,20,30,0.95)',
        borderRadius: '12px',
        padding: '16px',
        border: voteSubmitted ? '1px solid rgba(100,100,120,0.2)' : '1px solid rgba(100,180,255,0.3)',
        animation: voteSubmitted ? 'none' : 'turnPulseGlow 2s ease-in-out infinite',
        opacity: voteSubmitted ? 0.5 : 1,
        transition: 'opacity 0.3s, background-color 0.3s, border-color 0.3s',
        boxSizing: 'border-box',
        maxWidth: '100%',
      }}>
        {pulseGlowStyle}
        <div style={{
          textAlign: 'center', marginBottom: '12px',
          color: voteSubmitted ? 'rgba(255,255,255,0.4)' : '#66bbff',
          fontWeight: 700, fontSize: '1.1em', letterSpacing: '1px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        }}>
          <span>{turnSubmitted ? 'Vote cast — waiting for the reveal...' : 'VOTE — who is The Glitch?'}</span>
          {secondsLeft != null && !turnSubmitted && (
            <span style={{ color: secondsLeft <= 10 ? '#ff6666' : 'rgba(255,255,255,0.7)', fontSize: '0.85em', fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: '32px' }}>
              {secondsLeft}s
            </span>
          )}
        </div>
        {error && (
          <div style={{ color: '#ff6666', textAlign: 'center', marginBottom: '10px', fontSize: '0.9em' }}>{error}</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
          {players.map((p) => (
            <button
              key={p.game_player_id || p.name}
              type="button"
              onClick={() => handlePickVote(p)}
              disabled={voteSubmitted}
              style={{
                padding: '10px 16px', borderRadius: '10px',
                border: '1px solid rgba(100,180,255,0.35)',
                backgroundColor: 'rgba(100,180,255,0.12)',
                color: voteSubmitted ? 'rgba(255,255,255,0.35)' : '#cfe8ff',
                cursor: voteSubmitted ? 'not-allowed' : 'pointer',
                fontWeight: 700, fontSize: '0.95em',
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // 🐛 Doodle Bug turn: the input IS the canvas — banner + open button; the
  // canvas modal (seeded with the shared drawing, 2-stroke cap) lives in
  // GameStepPage and submits via respondToInteraction.
  if (imposterMode && isYourTurn && pendingTurnType === 'doodle_turn' && onOpenDoodleTurn) {
    return (
      <div className="human-input-form" style={{
        backgroundColor: 'rgba(20,30,20,0.95)',
        borderRadius: '12px', padding: '16px',
        border: '1px solid rgba(139,195,74,0.4)',
        animation: 'turnPulseGlow 2s ease-in-out infinite',
        boxSizing: 'border-box', maxWidth: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', flexWrap: 'wrap',
      }}>
        {pulseGlowStyle}
        <span style={{ color: '#aed581', fontWeight: 700, fontSize: '1.05em', letterSpacing: '0.5px' }}>
          🐛 YOUR TURN{secondsLeft != null ? ` · ${secondsLeft}s` : ''}
        </span>
        <button
          type="button"
          onClick={onOpenDoodleTurn}
          style={{
            padding: '10px 22px', borderRadius: '22px', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #8bc34a, #558b2f)', color: '#fff',
            fontWeight: 800, fontSize: '0.95em', letterSpacing: '0.4px',
            boxShadow: '0 3px 14px rgba(139,195,74,0.4)',
          }}
        >
          🖍️ Add your 2 strokes
        </button>
      </div>
    );
  }

  // Imposter (Glitch) mode: simplified one-word input
  if (imposterMode && isYourTurn) {
    const handleWordSubmit = async (e) => {
      e.preventDefault();
      const word = message.trim().split(/\s+/)[0]; // Take first word only
      if (!word) {
        setError('Type one word!');
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        await onSubmit({
          action: 'word',
          message: word,
          thoughts: ''
        });
        setMessage('');
        if (isYourTurn) setTurnSubmitted(true);
      } catch (err) {
        setError(err.message || 'Failed to submit word');
      } finally {
        setIsSubmitting(false);
      }
    };

    const imposterSubmitted = turnSubmitted || isSubmitting;

    return (
      <form onSubmit={handleWordSubmit} className="human-input-form" style={{
        backgroundColor: imposterSubmitted ? 'rgba(40,40,50,0.7)' : 'rgba(20,20,30,0.95)',
        borderRadius: '12px',
        padding: '16px',
        border: imposterSubmitted ? '1px solid rgba(100,100,120,0.2)' : '1px solid rgba(100,180,255,0.3)',
        boxShadow: imposterSubmitted ? 'none' : undefined,
        animation: imposterSubmitted ? 'none' : 'turnPulseGlow 2s ease-in-out infinite',
        opacity: imposterSubmitted ? 0.5 : 1,
        transition: 'opacity 0.3s, background-color 0.3s, border-color 0.3s',
        boxSizing: 'border-box',
        maxWidth: '100%',
      }}>
        {pulseGlowStyle}
        <div style={{
          textAlign: 'center',
          marginBottom: '12px',
          color: imposterSubmitted ? 'rgba(255,255,255,0.4)' : '#66bbff',
          fontWeight: 700,
          fontSize: '1.1em',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}>
          <span>{turnSubmitted ? 'Word sent. Waiting for others...' : 'YOUR TURN · Say one word'}</span>
          {secondsLeft != null && (
            <span style={{
              color: secondsLeft <= 10 ? '#ff6666' : 'rgba(255,255,255,0.7)',
              fontSize: '0.85em',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              minWidth: '32px',
            }}>
              {secondsLeft}s
            </span>
          )}
        </div>

        {error && (
          <div style={{
            color: '#ff6666',
            textAlign: 'center',
            marginBottom: '8px',
            fontSize: '0.85em',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', maxWidth: '100%', overflow: 'hidden' }}>
          <input
            type="text"
            value={message}
            onChange={(e) => {
              // Only allow single word (no spaces)
              const val = e.target.value.replace(/\s/g, '');
              setMessage(val);
            }}
            // Cycle 5g.14 (Bug #1): explicit Enter-to-submit. Mirrors the
            // textarea pattern at lines 475/813/1068. Without this, the
            // <input> relies on browser implicit form-submission via Enter,
            // which CDP Input.dispatchKeyEvent does NOT reliably trigger —
            // 5g.13 smoke saw 3 react_type [+submit] calls with 0 backend
            // submissions. See [[play-game-glitch-modal-order-bug]] resolution.
            onKeyDown={(e) => handleKeyDownWithMention(e, handleWordSubmit)}
            placeholder="Type one word..."
            disabled={isSubmitting || disabled || turnSubmitted}
            autoFocus
            style={{
              flex: 1,
              minWidth: 0,
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(100,180,255,0.4)',
              backgroundColor: 'rgba(0,0,0,0.4)',
              color: '#fff',
              fontSize: '1.2em',
              fontWeight: 700,
              textAlign: 'center',
              letterSpacing: '1px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting || disabled || turnSubmitted || !message.trim()}
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: message.trim() ? '#66bbff' : 'rgba(100,180,255,0.3)',
              color: '#000',
              fontWeight: 700,
              fontSize: '1em',
              cursor: (isSubmitting || disabled || !message.trim()) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
          >
            {isSubmitting ? '...' : 'Say'}
          </button>
        </div>
      </form>
    );
  }

  // Competitive mode: simplified message input (full messages, not one-word)
  if (competitiveMode && isYourTurn) {
    const timerExpired = secondsLeft !== null && secondsLeft <= 0;

    const handleCompetitiveSubmit = async (e) => {
      e.preventDefault();
      if (timerExpired) return;
      const msg = message.trim();
      if (!msg) {
        setError('Type a message!');
        return;
      }
      // Word Ban — this competitive path bypassed validateForm entirely, so the ban never
      // fired on competitive turns (user-reported: sent "the" while banned from "the").
      const banErr = bannedWordError(msg);
      if (banErr) { setError(banErr); return; }
      setIsSubmitting(true);
      setError(null);
      try {
        const targetPlayers = extractTargetPlayers(msg);
        await onSubmit({
          action: 'public_message',
          message: msg,
          thoughts: '',
          ...(targetPlayers.length > 0 ? { target_players: targetPlayers } : {})
        });
        setMessage('');
        setShowMentionDropdown(false);
        if (isYourTurn) setTurnSubmitted(true);
      } catch (err) {
        setError(err.message || 'Failed to send message');
      } finally {
        setIsSubmitting(false);
      }
    };

    const compSubmitted = turnSubmitted || isSubmitting;
    const compInactive = compSubmitted || timerExpired;
    // Locked until the player rolls their Fate die. We keep the form readable (no dim) but
    // kill its glow so attention goes to the pulsing die, and show the lock on the box.
    const lockedOut = locked && !compSubmitted && !timerExpired;

    return (
      <form onSubmit={handleCompetitiveSubmit} className="human-input-form" style={noirDocket ? {
        // 🗂 Docket mode: the GameStepPage case-dock wrapper IS the card — no second chrome.
        backgroundColor: 'transparent', border: 'none', padding: 0, boxShadow: 'none',
        opacity: compInactive ? 0.55 : 1,
        transition: 'opacity 0.3s',
      } : {
        backgroundColor: compSubmitted ? 'rgba(40,40,50,0.7)' : 'rgba(20,20,30,0.95)',
        borderRadius: '12px',
        padding: '16px',
        border: timerExpired ? '1px solid rgba(255,100,100,0.3)' : compSubmitted ? '1px solid rgba(100,100,120,0.2)' : '1px solid rgba(100,180,255,0.3)',
        boxShadow: compInactive ? 'none' : undefined,
        animation: (compInactive || lockedOut) ? 'none' : 'turnPulseGlow 2s ease-in-out infinite',
        opacity: compInactive ? 0.5 : 1,
        transition: 'opacity 0.3s, background-color 0.3s, border-color 0.3s',
      }}>
        {pulseGlowStyle}
        {noirDocket ? (
          <>
            {/* Manila tab — bleeds to the case-dock wrapper's edges (its padding is
                10px 12px); same paper as the I'M READY desk band. One clock, penalty
                already applied server-side — no arithmetic for the player. */}
            <div style={{
              margin: '-10px -12px 10px', padding: '7px 14px',
              borderRadius: '13px 13px 0 0',
              background: '#cdb98a', color: '#2a2014',
              textShadow: 'none', // 🗒️ paper resets the halo (roomHaloPaper.test.js)
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              fontWeight: 800, fontSize: '0.72em', letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span>🖋 {turnSubmitted ? 'Sent. The room reacts.' : timerExpired ? "Time's up." : 'Your move'}</span>
              {secondsLeft != null && !timerExpired && (
                <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.2em', fontWeight: 900, color: secondsLeft <= 10 ? '#8a1f1f' : '#2a2014' }}>
                  ⏱ {secondsLeft}s
                </span>
              )}
            </div>
            {!compInactive && (
              <div style={{ padding: '0 2px 8px' }}>
                <div style={{ color: '#eed9a4', fontWeight: 900, fontSize: '1.05em', letterSpacing: '0.02em' }}>{noirDocket.headline}</div>
                {Array.isArray(noirDocket.facts) && noirDocket.facts.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 4, fontSize: '0.8em', alignItems: 'baseline' }}>
                    {noirDocket.facts.map((f, i) => (
                      <span key={i} style={{ color: f.c || 'rgba(255,255,255,0.55)', fontWeight: 700, fontStyle: f.whisper ? 'italic' : 'normal' }}>{f.t}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
        <div style={{
          textAlign: 'center',
          marginBottom: '12px',
          color: compSubmitted ? 'rgba(255,255,255,0.4)' : timerExpired ? '#ff6666' : '#66bbff',
          fontWeight: 700,
          fontSize: '1.1em',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}>
          <span>{turnSubmitted ? 'Message sent. Waiting for others...' : timerExpired ? 'TIME\'S UP. Turn skipped' : lockedOut ? 'YOUR TURN 🎲' : 'YOUR TURN · Send a message'}</span>
          {secondsLeft != null && !timerExpired && (
            <span style={{
              color: secondsLeft <= 10 ? '#ff6666' : 'rgba(255,255,255,0.7)',
              fontSize: '0.85em',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              minWidth: '32px',
            }}>
              {secondsLeft}s
            </span>
          )}
        </div>
        )}

        {!compSubmitted && renderBanNotice()}

        {/* Side quest — the dare lives WHERE you write, not in a feed you scrolled past.
            Claimed state flips live (parent grades on server ack) for the instant payoff.
            Docket mode: the 3-line dashed box becomes ONE slim line (Nina rev 3);
            07-30: hidden until the first keystroke (docketDareSeen above). */}
        {noirDocket && sideQuest && !timerExpired && !lockedOut && (sideQuest.done || (!compSubmitted && docketDareSeen)) && (
          <div style={{
            margin: '0 0 8px', padding: '5px 2px', fontSize: '0.8em', lineHeight: 1.4,
            borderTop: '1px solid rgba(217,186,114,0.18)', borderBottom: '1px solid rgba(217,186,114,0.18)',
          }}>
            {sideQuest.done ? (
              <span style={{ color: '#7fd48f', fontWeight: 800 }}>✅ +{sideQuest.points} claimed. You won the bet.</span>
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                🎯 <span style={{ color: '#c9b06a', fontWeight: 800 }}>{sideQuest.label || "Detective's bet"}:</span>{' '}
                <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700 }}>{sideQuest.text}</span>{' '}
                <span style={{ color: '#facc15', fontWeight: 800 }}>+{sideQuest.points}</span> · any message counts
              </span>
            )}
          </div>
        )}
        {/* 🎯 Detective's-bet reminder (Nina 07-31) — shown ONLY while the viewer's
            accepted bet is live this round. Opt-in means it's wanted, so no
            hide-until-keystroke here; still one slim line, composer stays clean. */}
        {noirDocket && betReminder && !timerExpired && !lockedOut && (
          <div style={{
            margin: '0 0 8px', padding: '5px 2px', fontSize: '0.8em', lineHeight: 1.4,
            borderTop: '1px solid rgba(217,186,114,0.18)', borderBottom: '1px solid rgba(217,186,114,0.18)',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>
              🎯 <span style={{ color: '#c9b06a', fontWeight: 800 }}>Your bet is live:</span>{' '}
              <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700 }}>"{betReminder.word}"</span>{' '}
              <span style={{ color: '#facc15', fontWeight: 800 }}>+{betReminder.points}</span> · the detective is listening
            </span>
          </div>
        )}
        {!noirDocket && sideQuest && !timerExpired && !lockedOut && (
          sideQuest.done ? (
            <div style={{
              margin: '0 0 10px', padding: '8px 14px', borderRadius: '10px',
              border: '1.5px solid #22c55e', backgroundColor: '#22c55e26',
              color: '#4ade80', fontWeight: 800, fontSize: '0.9em', textAlign: 'center',
            }}>
              ✅ +{sideQuest.points} claimed! {sideQuest.label ? 'You won the bet.' : 'Side quest complete.'}
            </div>
          ) : !compSubmitted && (
            <div style={{
              margin: '0 0 10px', padding: '9px 14px', borderRadius: '10px',
              border: '1.5px dashed #22c55e', backgroundColor: '#22c55e14',
              textAlign: 'center', lineHeight: 1.45,
            }}>
              <div style={{ color: '#4ade80', fontWeight: 800, fontSize: '0.78em', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                🎯 {sideQuest.label || 'SIDE QUEST'} <span style={{ color: '#facc15' }}>+{sideQuest.points}</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 600, fontSize: '0.95em', marginTop: '2px' }}>
                {sideQuest.text}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75em', marginTop: '2px' }}>
                Optional bonus. Any message this round counts.
              </div>
            </div>
          )
        )}

        {error && (
          <div style={{
            color: '#ff6666',
            textAlign: 'center',
            marginBottom: '8px',
            fontSize: '0.85em',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '8px', position: 'relative' }}>
          {renderMentionDropdown()}
          <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => { if (noirDocket && !docketDareSeen) setDocketDareSeen(true); handleMessageChange(e); }}
              onFocus={handleComposerFocus}
              onBlur={handleComposerBlur}
              onKeyDown={(e) => handleKeyDownWithMention(e, handleCompetitiveSubmit)}
              placeholder={lockedOut
                ? '🎲 Roll your die to play your turn…'
                : replyCredit
                  ? '💬 Reply credit: answer someone by name, quote them, hit back…'
                  : sentenceCap
                    ? 'Keep it under ~40 words. Make every one land… (@ to mention)'
                    : 'What would you like to say? (@ to mention)'}
              disabled={isSubmitting || disabled || turnSubmitted || timerExpired || lockedOut}
              autoFocus={!lockedOut}
              maxLength={sentenceCap ? Math.min(LIMITS.CHAT_MESSAGE_MAX, sentenceCap * 140) : LIMITS.CHAT_MESSAGE_MAX}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: '8px',
                border: lockedOut ? '1px dashed rgba(255,255,255,0.25)' : noirDocket ? '1px solid rgba(217,186,114,0.35)' : '1px solid rgba(100,180,255,0.4)',
                backgroundColor: lockedOut ? 'rgba(0,0,0,0.25)' : noirDocket ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.4)',
                color: '#fff',
                fontSize: isMobile ? '1em' : '0.95em',
                fontFamily: 'inherit',
                minHeight: '96px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                opacity: lockedOut ? 0.55 : 1,
                cursor: lockedOut ? 'not-allowed' : 'text',
                transition: 'opacity 0.2s, border-color 0.2s, background-color 0.2s',
              }}
            />
            {lockedOut && (
              <div
                onClick={() => { if (!diceRolling && onRollDie) onRollDie(); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !diceRolling && onRollDie) { e.preventDefault(); onRollDie(); } }}
                title={`Roll your ${diceLabel || 'FATE ROLL'}`}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  cursor: diceRolling ? 'default' : 'pointer',
                  borderRadius: '8px',
                  background: 'rgba(10,12,24,0.72)',
                  border: '2px solid rgba(102,187,255,0.85)',
                  boxShadow: '0 0 16px rgba(100,180,255,0.45)',
                  userSelect: 'none',
                }}
              >
                <style>{`@keyframes hifDiceTumble { 0%{transform:rotate(0) scale(1)} 25%{transform:rotate(160deg) scale(1.3)} 50%{transform:rotate(300deg) scale(0.82)} 75%{transform:rotate(520deg) scale(1.25)} 100%{transform:rotate(720deg) scale(1)} } @keyframes hifDiceWiggle { 0%,100%{transform:rotate(-8deg)} 50%{transform:rotate(8deg)} }`}</style>
                <span style={{ fontSize: '2em', lineHeight: 1, animation: diceRolling ? 'hifDiceTumble 0.9s ease-in-out' : 'hifDiceWiggle 1.2s ease-in-out infinite' }}>🎲</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: '1em', letterSpacing: '0.5px' }}>{diceLabel || 'FATE ROLL'}</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.78em' }}>{diceRolling ? 'rolling…' : 'tap to roll'}</div>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: '4px', alignSelf: isMobile ? 'stretch' : 'flex-end' }}>
            <button
              type="submit"
              disabled={isSubmitting || disabled || turnSubmitted || timerExpired || lockedOut || !message.trim()}
              style={{
                flex: isMobile ? 1 : undefined,
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: noirDocket
                  ? ((timerExpired || lockedOut) ? 'rgba(217,186,114,0.15)' : message.trim() ? '#d9ba72' : 'rgba(217,186,114,0.3)')
                  : ((timerExpired || lockedOut) ? 'rgba(100,180,255,0.15)' : message.trim() ? '#66bbff' : 'rgba(100,180,255,0.3)'),
                color: (timerExpired || lockedOut) ? 'rgba(255,255,255,0.3)' : noirDocket ? '#151009' : '#000',
                fontWeight: 700,
                fontSize: '1em',
                cursor: (isSubmitting || disabled || turnSubmitted || timerExpired || lockedOut || !message.trim()) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {isSubmitting ? '...' : 'Send'}
            </button>
            <button
              type="button"
              disabled={isSubmitting || disabled || turnSubmitted || timerExpired}
              onClick={async () => {
                setIsSubmitting(true);
                setError(null);
                try {
                  await onSubmit({ action: 'pass', message: '', thoughts: '' });
                  setMessage('');
                  if (isYourTurn) setTurnSubmitted(true);
                } catch (err) {
                  setError(err.message || 'Failed to skip');
                } finally {
                  setIsSubmitting(false);
                }
              }}
              style={noirDocket ? {
                // Docket mode: skip survives as a quiet link (Nina rev 3), not a second button.
                padding: '4px 2px', border: 'none', backgroundColor: 'transparent',
                color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.78em',
                textDecoration: 'underline', fontFamily: 'inherit',
                cursor: (isSubmitting || disabled || turnSubmitted || timerExpired) ? 'not-allowed' : 'pointer',
              } : {
                padding: '8px 20px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.25)',
                backgroundColor: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.75)',
                fontWeight: 700,
                fontSize: '0.85em',
                cursor: (isSubmitting || disabled || turnSubmitted || timerExpired) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {noirDocket ? 'skip' : 'Skip Turn'}
            </button>
          </div>
        </div>

        {/* Play Round button */}
        {playRoundButton && <div style={{ marginTop: '10px' }}>{playRoundButton}</div>}
      </form>
    );
  }

  // Full action mode (when it's your turn)
  return (
    <form onSubmit={handleSubmit} className="human-input-form" style={{
      backgroundColor: THEME.bgLight,
      borderRadius: '8px',
      padding: '12px',
      boxShadow: `0 2px 8px ${THEME.textDark}1a`
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: `2px solid ${THEME.accent}`
      }}>
        <h3 style={{
          margin: '0 0 6px 0',
          color: THEME.primary,
          fontSize: '1em'
        }}>
          Your Turn, {playerName}!
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '0.85em',
          color: THEME.textMuted
        }}>
          <span>
            Credits: <strong style={{ color: tokensLeft < 3 ? THEME.error : THEME.success }}>
              {tokensLeft}
            </strong>
          </span>
          <span>
            Cost: <strong style={{ color: canAfford ? THEME.textDark : THEME.error }}>
              {currentActionCost}
            </strong>
          </span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: `${errorTone}1a`,
          color: errorTone,
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          border: `1px solid ${errorTone}`
        }}>
          {error}
        </div>
      )}

      {/* Action Type Selector */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{
          display: 'block',
          marginBottom: '6px',
          fontWeight: 'bold',
          color: THEME.textDark,
          fontSize: '0.9em'
        }}>
          Action Type:
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '6px'
        }}>
          {availableActions.map(action => {
            const isSelected = actionType === action.action;
            const cost = action.cost_tokens || 0;
            const affordable = tokensLeft >= cost;

            return (
              <button
                key={action.action}
                type="button"
                onClick={() => setActionType(action.action)}
                disabled={!affordable}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: `2px solid ${isSelected ? THEME.primary : THEME.borderLight}`,
                  backgroundColor: isSelected ? THEME.primary : THEME.bgMedium,
                  color: isSelected ? THEME.textLight : THEME.textDark,
                  cursor: affordable ? 'pointer' : 'not-allowed',
                  opacity: affordable ? 1 : 0.5,
                  transition: 'all 0.2s',
                  fontSize: '0.85em'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                  {action.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div style={{ fontSize: '0.8em', opacity: 0.9 }}>
                  {cost} credit{cost !== 1 ? 's' : ''}
                </div>
              </button>
            );
          })}
        </div>
        {availableActions.find(a => a.action === actionType) && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: THEME.bgMedium,
            borderRadius: '4px',
            fontSize: '0.85em',
            color: THEME.textMuted
          }}>
            {availableActions.find(a => a.action === actionType).description}
          </div>
        )}
      </div>

      {/* Recipient Selector (for private/secret messages) */}
      {(actionType === 'private_message' || actionType === 'secret_message') && possibleRecipients.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Recipients:
          </label>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {possibleRecipients.map(recipient => {
              const isSelected = recipients.includes(recipient);
              return (
                <button
                  key={recipient}
                  type="button"
                  onClick={() => toggleRecipient(recipient)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: `2px solid ${isSelected ? THEME.secondary : THEME.borderLight}`,
                    backgroundColor: isSelected ? THEME.secondary : THEME.bgMedium,
                    color: isSelected ? THEME.textLight : THEME.textDark,
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    transition: 'all 0.2s'
                  }}
                >
                  {recipient}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Message Input (hidden for pass action) */}
      {actionType !== 'pass' && (
        <div style={{ marginBottom: '10px', position: 'relative' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontWeight: 'bold',
            color: THEME.textDark,
            fontSize: '0.9em'
          }}>
            Message: <span style={{ color: THEME.error }}>*</span>
          </label>
          {renderMentionDropdown()}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onFocus={handleComposerFocus}
            onBlur={handleComposerBlur}
            onKeyDown={(e) => handleKeyDownWithMention(e, handleSubmit)}
            placeholder="What would you like to say? (@ to mention, Enter to send)"
            disabled={isSubmitting || disabled || turnSubmitted}
            maxLength={LIMITS.CHAT_MESSAGE_MAX}
            style={{
              width: '100%',
              minHeight: '60px',
              padding: '8px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`,
              backgroundColor: 'rgba(0,0,0,0.4)',
              color: '#fff',
              fontSize: isMobile ? '1em' : '0.9em',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
          <div style={{
            textAlign: 'right',
            fontSize: '0.85em',
            marginTop: '4px',
            color: message.length > LIMITS.CHAT_MESSAGE_MAX * 0.9 ? THEME.error : THEME.textMuted,
            fontWeight: message.length > LIMITS.CHAT_MESSAGE_MAX * 0.9 ? 700 : 400
          }}>
            {message.length}/{LIMITS.CHAT_MESSAGE_MAX}
          </div>
        </div>
      )}

      {/* Thoughts Input (optional, internal) */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{
          display: 'block',
          marginBottom: '4px',
          fontWeight: 'bold',
          color: THEME.textDark,
          fontSize: '0.9em'
        }}>
          Private Thoughts: <span style={{ fontSize: '0.8em', fontWeight: 'normal', color: THEME.textMuted }}>(optional)</span>
        </label>
        <textarea
          value={thoughts}
          onChange={(e) => setThoughts(e.target.value)}
          placeholder="Your internal reasoning..."
          disabled={isSubmitting || disabled || turnSubmitted}
          maxLength={LIMITS.THOUGHTS_MAX}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '8px',
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`,
            fontSize: '0.85em',
            fontFamily: 'inherit',
            resize: 'vertical',
            backgroundColor: `${THEME.warning}1a`,
            color: '#fff',
            boxSizing: 'border-box'
          }}
        />
        <div style={{
          textAlign: 'right',
          fontSize: '0.8em',
          marginTop: '2px',
          color: thoughts.length > LIMITS.THOUGHTS_MAX * 0.9 ? THEME.error : THEME.textMuted,
          fontWeight: thoughts.length > LIMITS.THOUGHTS_MAX * 0.9 ? 700 : 400
        }}>
          {thoughts.length}/{LIMITS.THOUGHTS_MAX}
        </div>
      </div>

      {/* Play Round button (injected from parent) */}
      {playRoundButton && <div style={{ marginBottom: '6px' }}>{playRoundButton}</div>}

      {/* Submit Button */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
      }}>
        <button
          type="submit"
          disabled={isSubmitting || disabled || turnSubmitted || !canAfford}
          style={{
            flex: 1,
            padding: '10px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: canAfford ? THEME.primary : THEME.textMuted,
            color: THEME.textLight,
            fontSize: '0.9em',
            fontWeight: 'bold',
            cursor: (isSubmitting || disabled || turnSubmitted || !canAfford) ? 'not-allowed' : 'pointer',
            opacity: (isSubmitting || disabled || turnSubmitted || !canAfford) ? 0.6 : 1,
            transition: 'all 0.2s'
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Action'}
        </button>

        {actionType === 'pass' && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: THEME.warning + '20',
            color: THEME.warning,
            borderRadius: '4px',
            fontSize: '0.85em',
            fontWeight: 'bold'
          }}>
            You will skip your turn
          </div>
        )}
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '8px',
        padding: '6px',
        backgroundColor: THEME.bgMedium,
        borderRadius: '4px',
        fontSize: '0.75em',
        color: THEME.textMuted,
        lineHeight: '1.3'
      }}>
        💡 Private messages: recipients see content, others see "[Private message]". Secret messages: completely hidden.
      </div>
    </form>
  );
};

export default HumanInputForm;
